<template>
    <div class="module-chart" :class="{ 'chart-inner': showChart }">
        <component-loader v-if="!moduleCreated || showChanging" />
        <div class="title" v-if="hasTitle">
            {{props.title}}
        </div>
        <base-chart
            :class="{ 'has-title': hasTitle }"
            :constructor-type="hasTime ? 'stockChart' : 'chart'"
            v-if="moduleCreated && chartOptions"
            :initialOptions="chartOptions"
            v-show="showChart && !showNoData && !showChanging"
            :key="keyCounter"
            :isUsingBoost="isUsingBoost"
            @rerequest="showRerequest"
            @hasBoost="setIsUsingBoost"
            ref="baseChartRef"
            :callback="() => {}"
            :updateArgs="[true, true]"
            skip-automatic-theme-update
        />
        <div v-show="showTable && moduleCreated && !showNoData && !showChanging" class="module-table">
            <div class="module-table-inner">
                <generic-table
                    v-if="tableData && !props.chartData"
                    ref="genericTableRef"
                    :data="tableData"
                    small-header
                    hide-button-row
                    prevent-download
                    :filename="props.title"
                />
                <chart-data-table
                    ref="chartDataTableRef"
                    v-else-if="props.chartData && props.chartConfig"
                    :chart-config="props.chartConfig"
                    :chart-data="props.chartData"
                    :hidden-headings="hiddenHeadings"
                    :prevent-download="!props.allowModuleChartTableExcelDownload"
                    :hide-top-filters="props.allowModuleChartTableExcelDownload"
                    :excel-filename="props.title"
                    small-header
                />
            </div>
            <div v-if="tableIncludesHistoric" class="data-changing-table">
                This table will not receive live updates due to containing historic data.
            </div>
        </div>
        <module-chart-metrics
            v-if="showMetrics && moduleCreated && !showNoData && !showChanging"
            :metrics-data="metricsData"
            :title="props.title || props.chartConfig.info.name"
            :metric-calc-data="metricCalcData"
            :calculating-metrics="calculatingMetrics"
            @recalculate="getSummaryMetrics"
        />
        <module-access-denied
            v-else-if="allNoAccess"
            message="You do not have access to this chart."
            :access-level-messages="Object.values(seriesWithAccessLevels)"
        />
        <div class="no-data" v-else-if="standardNoData">{{noDataText}}</div>
        <div class="data-changing top hidden-md flex-column-with-gap" v-if="showChart && moduleCreated && !showNoData && !showChanging">
            <div v-if="hasDrilldown && !hideDrilldownText && !drilldownDisabled" class="drilldown">Click an area to drilldown</div>
            <icofont-icon
                v-if="isAggregate"
                icon="icofont-exclamation-circle"
                icon-colour="var(--lcpWatermelon)"
                v-tooltip="{ value: aggregateHelpText }"
            />
            <primevue-button
                v-if="isAggregate || rerequest"
                text
                class="refresh-button"
                :loading="dateChanging"
                @click="userRefresh"
                v-tooltip="isAggregate && !rerequest ? 'Live updates disabled in aggregate mode. Click to refresh.' : 'Live updates disabled. Click to refresh.'"
            >
                <font-awesome-icon :icon="['fas', dateChanging ? 'ellipsis' : 'refresh']" />
            </primevue-button>
            <icofont-icon
                v-if="showNoAccess"
                icon="icofont-exclamation-circle"
                icon-colour="var(--lcpWatermelon)"
                v-tooltip="{ value: partialAccessMissing }"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import minMax from 'dayjs/plugin/minMax';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import {
    inject, onBeforeUnmount, ref, computed, watch, nextTick,
} from 'vue';
import { SKIP_DASHBOARD_PALETTE_REVISION_KEY } from '@/composables/powerMarkets/dashboardPaletteRemount.ts';
import {
    Options, PointOptionsObject, Series, SeriesOptionsType, XAxisOptions, YAxisOptions, YAxisPlotLinesOptions,
} from 'highcharts';
import { getTheme } from '@/utils/chartGlobalSettings.ts';
import PrimevueButton from 'primevue/button';
import { cloneDeep } from '@/utils/global.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useUserSettingsActions } from '@/actions/powerMarkets/userSettingsActions.ts';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { resolveStaticApiEndpoint } from '@/api/powerMarkets/staticApiOverrides.ts';
import BaseChart from '@/components/chart/BaseChart.vue';
import {
    IModuleChartRequestData, IModuleChartRequestDataSeries, IModuleChartRequestPayload, IModuleChartConfig,
    IAxisLimits,
    IModuleChartMetricsPayload,
    IModuleChartMetrics,
    ICombinedAggregateOptions,
    ViewMode,
} from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import { getPeriodFromTime, getTimeFromPeriod } from '@/utils/periodUtils.ts';
import { IChartPush } from '@/interfaces/powerMarkets/charts/IChartPush.ts';
import { ITableSeries } from '@/interfaces/powerMarkets/table/ITableSeries.ts';
import {
    getHighChartsYValue, getChartOptions, getTooltip,
    ISeriesDataTooltipConfig, getSyncTooltipHover, getYAxisZeroPlotLine,
    getMultiYAxisGridColour, applyDateTimeLabelFormats,
} from '@/helpers/charts/chartSettings.ts';
import { getDatesFromConfig } from '@/helpers/datePickerMethod.ts';
import { compareDdMmYyyyDateStrings, getDayJsRangeFromDayOnlyDatePicker } from '@/utils/dateUtils.ts';
import {
    mergeRealtimeDayBuckets, writeHistoricSqlIfAllowed,
} from '@/helpers/charts/filterSeriesPointsByExistingX.ts';
import { ISeriesFrontEnd } from '@/interfaces/powerMarkets/series/ISeriesFrontEnd.ts';
import { ISeriesWithExtraOptions } from '@/interfaces/powerMarkets/charts/ISeriesWithExtraOptions.ts';
import { useApi, apiAbortOptions } from '@/api/useApi.ts';
import { isAbortError } from '@/helpers/isAbortError.ts';
import { ISeriesList } from '@/interfaces/powerMarkets/series/ISeriesList.ts';
import { IGenericTableData } from '@/interfaces/powerMarkets/table/IGenericTableData.ts';
import { IDateRangeComposite } from '@/interfaces/general/IDateRange.ts';
import { IAdvancedDatePickerModel } from '@/interfaces/general/IAdvancedDatePickerModel.ts';
import { IDateRangeByNumberWithType } from '@/interfaces/general/IDateRangeByString.ts';
import { chartApi, IGetSeriesSummaryPayload } from '@/api/powerMarkets/chartApi.ts';
import { apiEndpointUrls } from '@/api/apiEndpointConfig.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import {
    cancelChartPaletteRefresh,
    enqueueChartPaletteRefresh,
} from '@/composables/chartPaletteRefreshQueue.ts';
import type { ColourPaletteMode } from '@/constants/ColourPalettes.ts';
import {
    useChartSync, useYAxisSync, useLegendSync, computeSharedYAxisExtents,
} from '@/composables/powerMarkets/useChartCommunication.ts';
import { IChartSeriesAdditionalDetails } from '@/interfaces/powerMarkets/IModule.ts';
import { IExtraChartInfo } from '@/interfaces/powerMarkets/dashboard/IDashboardPresetItemInfo.ts';
import { IDashboardOverrides } from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import ComponentLoader from '@/components/layout/ComponentLoader.vue';
import { useSignalRLifecycle } from '@/realTimeConnection/SignalRLifecycle.ts';
import { Messages } from '@/constants/Messages.ts';
import { aggregateGranularities } from '@/constants/powerMarkets/ChartAggregateOptions.ts';
import {
    getClearedAggregateOptions,
    getDefaultAggregateOptionsFromAdditionalDetails,
    hasProvidedAggregateOptions,
} from '@/helpers/charts/aggregateOptionsFromAdditionalDetails.ts';
import { promiseWithTimeout } from '@/utils/promiseUtils.ts';
import { genericTableDataToCsvRows } from '@/utils/reportExportUtils.ts';
import { findEndpointBufferDateKey } from '@/helpers/charts/endpointHostPairs.ts';
import {
    LegendItemClickHandler,
    resolveIsolateVisibility,
} from '@/helpers/charts/legendItemClick.ts';

import {
    DEFAULT_PERIODS_PER_DAY, DEFAULT_TIMEZONE, HOURS_PER_DAY, MINUTES_PER_HOUR,
} from '@/constants/Time.ts';
import { IDateObject } from '@/interfaces/powerMarkets/IDateObject.ts';
import IcofontIcon from '@/components/powerMarkets/common/IcofontIcon.vue';
import GenericTable from '@/components/powerMarkets/tables/GenericTable.vue';
import ChartDataTable from '@/components/powerMarkets/tables/ChartDataTable.vue';
import ModuleChartMetrics from '@/components/powerMarkets/chart/ModuleChartMetrics.vue';
import ModuleAccessDenied from '@/components/powerMarkets/ModuleAccessDenied.vue';
import { NumericMatrixMap, SeriesDataRaw } from '@/types/powerMarkets/SeriesData.ts';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(minMax);
dayjs.extend(isSameOrBefore);
dayjs.extend(minMax);
dayjs.extend(isBetween);

// #region props

const props = withDefaults(defineProps<{
    activeViewMode?: ViewMode;
    currentViewIndex?: number;
    currentModuleIndex?: number;
    chartConfig: IModuleChartConfig;
    chartData?: ISeriesList | null;
    title?: string;
    chartLegendHeightOverride?: number | null;
    fixedLegend?: boolean;
    moveLegendRight?: boolean;
    clearSeriesOnRemove?: boolean;
    noPushes?: boolean;
    isDelta?: boolean;
    nonDeltaSeries?: ISeriesList | null;
    hideNavigator?: boolean;
    disableDrilldownProp?: boolean;
    allowModuleChartTableExcelDownload?: boolean;
    customDateOverride?: IDashboardOverrides | null;
    seperateTooltip?: boolean;
    // chartWidth?: number; // TODO: Do we want to bring what this did back? E.g. it used to cause x axis title to be hidden etc. See old ModuleChart
    // chartHeight?: number; // TODO: Do we want to bring what this did back? E.g. it used to cause x axis title to be hidden etc. See old ModuleChart
    isSmallWidth?: boolean;
    isSmallHeight?: boolean;
    currencyOverride?: string;
    manualTitle?: boolean;
    noDataMessage?: string | null;
    calculatorPreview?: boolean;
    isFixedModule?: boolean;
    isForIndex?: boolean;
    allowSelectTimezone?: boolean;
    forceLightTheme?: boolean;
    yAxisNormalizationGroupKey?: string | number;
}>(), {
    activeViewMode: ViewMode.Chart,
    currentViewIndex: -1,
    currentModuleIndex: -1,
    chartData: null,
    title: '',
    chartLegendHeightOverride: null,
    moveLegendRight: false,
    clearSeriesOnRemove: false,
    noPushes: false,
    isDelta: false,
    nonDeltaSeries: null,
    hideNavigator: false,
    disableDrilldownProp: false,
    allowModuleChartTableExcelDownload: false,
    customDateOverride: null,
    seperateTooltip: false,
    currencyOverride: undefined,
    noDataMessage: null,
    isFixedModule: false,
    isForIndex: false,
    allowSelectTimezone: false,
    forceLightTheme: false,
    yAxisNormalizationGroupKey: undefined,
});

// #endregion

// #region emits
const emit = defineEmits<{
    'close': [string];
    'edit': [string];
    'open:plant-modal': [string];
    'open:plant-drilldown-modal': [string, string];
    'update:extra-chart-info': [IExtraChartInfo];
    'update:isAggregate': [boolean];
    'update:no-data': [boolean];
    'update:no-access': [boolean];
}>();

// #endregion

interface ISeriesDataCache {
    configKey: string;
    seriesDataVersion: number;
    seriesData: NumericMatrixMap;
}

interface IDateRequestOne {
    from: Dayjs | null;
    to: Dayjs | null;
}

interface IDateRequestTwo {
    runSql: boolean;
    runBuffer: boolean;
    fromSql: Dayjs | null;
    toSql: Dayjs | null;
    fromBuffer: Dayjs | null;
    toBuffer: Dayjs | null;
}

interface IAxisToAdd {
    endOnTick: boolean;
    crosshair?: boolean;
    labels?: { style: { fontSize?: string; color?: string } };
    ordinal?: boolean;
    type: string;
    title?: { style: { fontSize: string; color?: string }; margin: number; text?: string };
    stackLabels?: { enabled: boolean };
    offset?: number;
    top?: string;
    height?: string;
    opposite?: boolean;
    lineWidth?: number;
    minorGridLineWidth?: number;
    minorTickLength?: number;
    lineColor?: string;
    gridLineColor?: string;
    tickLength?: number;
    plotLines?: Array<YAxisPlotLinesOptions>;
}

interface IAddSeriesAndAccessLevelsData {
    seriesFrontEnd?: ISeriesFrontEnd;
    SeriesFrontEnd?: ISeriesFrontEnd;
    accessLevelsFrontEnd?: {[key: string]: { accessLevel: string; hasAccess: boolean; seriesName: string }};
    AccessLevelsFrontEnd?: {[key: string]: { accessLevel: string; hasAccess: boolean; seriesName: string }};
    pointRange?: number | null;
    pointPlacement?: number | string | null;
}

// #region stores etc.

const userSettingsStore = useUserSettingsStore();
const dataStore = useDataStore();
const uiStore = useUIStore();
const skipDashboardPaletteRevision = inject(SKIP_DASHBOARD_PALETTE_REVISION_KEY, false);
const chartPaletteRefreshToken = Symbol('moduleChartPaletteRefresh');
const lastAppliedChartTheme = ref<ColourPaletteMode>(uiStore.theme);
const userSettingsActions = useUserSettingsActions();
const signalRLifecycle = useSignalRLifecycle();
const chartSync = useChartSync();
const yAxisSync = useYAxisSync(computed(() => props.yAxisNormalizationGroupKey));
const legendSync = useLegendSync();
const chartUsesDarkTheme = computed<boolean>(() => uiStore.theme === 'dark' && !props.forceLightTheme);

// #endregion

// #region variables

const genericTableRef = ref<InstanceType<typeof GenericTable> | null>(null);
const chartDataTableRef = ref<InstanceType<typeof ChartDataTable> | null>(null);
const hasDataLabel = ref(false);
const keyCounter = ref(0);
const numberOfYAxis = ref(0);
const numberOfXAxis = ref(0);
const chartOptions = ref<Highcharts.Options>();
const hasColumnAndOrdinal = ref(false);
const fromDate = ref('');
const toDate = ref('');
const currentHourStart = ref<number|null>(null);
const currentHourEnd = ref<number|null>(null);
const moduleCreated = ref(false);
const rerequest = ref(false);
const dateChanging = ref(false);
const metricCalcData = ref<string | null>(null);
const dateDisplay = ref(dayjs().format('LL'));
const displayDateRange = ref(false);
const displayTimeSelector = ref(false);
const hasDrilldown = ref(false);
const noDataTextOverride = ref(null);
const showNoData = ref(false);
const showChanging = ref(false);
const showTable = ref(false);
const showMetrics = ref(false);
const showChart = ref(true);
const showTime = ref(true);
const showLegend = ref(true);
const currentTimeLine = ref('period');
const hiddenHeadings = ref<Array<string>>([]);
const dataInterval = ref<ReturnType<typeof setInterval> | null>(null);
const metricsData = ref<IModuleChartMetrics[]>([]);
const seriesWithData = ref<Array<string>>([]);
const hasScatter = ref(false);
const calculatingMetrics = ref(false);
const hasTime = ref(false);
const hasNonTime = ref(false);
const aggregateOptionsChart = ref<ICombinedAggregateOptions | null>(null);
const imageModalConfigVisible = ref(false);
const yAxisLimitsModalVisible = ref(false);
const chartHelpModalVisible = ref(false);
const currencyList = ref(new Set<string>());
const isProcessingPush = ref(false);
const historicData = ref<NumericMatrixMap>({});
const realTimeData = ref<SeriesDataRaw>({});
const rawRealTimeData = ref<SeriesDataRaw>({});
const request = ref<Record<string, Array<IModuleChartRequestPayload>>>({});
const tableData = ref<IGenericTableData>();
const propData = ref<SeriesDataRaw>({});
const currentTimeInterval = ref<ReturnType<typeof setInterval> | null>(null);
const chartAbortController = ref<AbortController | null>(null);
const seriesWithAccessLevels = ref<Record<string, { seriesName: string; accessLevel: string }>>({});
const isUsingBoost = ref(false);
const hideDrilldownText = ref(true);
const showNoAccess = ref(false);
const showAllNoAccess = ref(true);
const yAxisEnumToIndex = ref<Record<string, number>>({});
const calculatingPercentiles = ref(false);
const currencyConversions = ref<Record<string, Record<string, number>>>({});
const tableIncludesHistoric = ref(false);
const calculatorTimer = ref<ReturnType<typeof setInterval> | null>(null); // Added as it wasn't initialised but created at runtime
const pointRangeOverride = ref<number | null>(null);
const pointPlacementOverride = ref<number | string | null>(null);
const baseChartRef = ref<typeof BaseChart | null>(null);

let combineConfigAndDataQueued = false;
let seriesDataVersion = 0;
let seriesDataCache: ISeriesDataCache | null = null;

const hasTitle = computed<boolean>(() => !showChanging.value && !!props.title && !!props.manualTitle);

// Forecast Evolution Mixin

const historyOfForecastConst = '@HoF';

function subscribeToForecastHistory (id: string, day: Dayjs, pushesSubscribed: Array<string>): void {
    const pushName = `forecastHistory&${id}&${day.format('YYYY-MM-DD')}`;
    if (pushesSubscribed.includes(pushName)) return;
    pushesSubscribed.push(pushName);
    signalRLifecycle.joinGroup('module', pushName);
}

// #endregion

async function initialise (): Promise<void> {
    chartAbortController.value = new AbortController();
    setChartOptions();
    setChartHover();
    setExtraChartInfo(true);

    await getDataAndCreateChart();
    dataInterval.value = setInterval(hourlyRefresh, 1000 * 60 * 30);
    moduleCreated.value = true;

    if (hasTime.value && hasNonTime.value) {
        setTimeout(updateRefOptions, 500);
    }

    const hasDisplayOptionOverride = props.chartConfig.info.extraChartInfo?.currentDisplayOption;
    if (hasDisplayOptionOverride) {
        applyCurrentView(props.activeViewMode);
    }
}

const debounceTableRefresh = useDebounceFn(() => {
    getTable();
}, 10000);

// Combines beforeDestroy and destroyed hooks
onBeforeUnmount(async () => {
    cancelChartPaletteRefresh(chartPaletteRefreshToken);
    clearInterval(dataInterval.value ?? undefined);
    clearMinuteInterval();

    if (calculatorTimer.value) {
        clearInterval(calculatorTimer.value);
    }

    legendClickHandler.clear();

    chartAbortController.value?.abort();
});

// #region computed

const fromToDateOverride = computed<IDateObject | undefined>(() => (props.customDateOverride?.chart));

const resolvedOverrideFromTo = computed<{ from: Date | null; to: Date | null }>(() => {
    const chart = fromToDateOverride.value;
    if (!chart?.from && !chart?.to) return { from: null, to: null };
    const { from, to } = getDatesFromConfig(chart);
    return {
        from: from?.isValid() ? from.toDate() : null,
        to: to?.isValid() ? to.toDate() : null,
    };
});

const fromDateOverride = computed<Date | null>(() => resolvedOverrideFromTo.value.from);
const toDateOverride = computed<Date | null>(() => resolvedOverrideFromTo.value.to);

const aggregateOptions = computed<ICombinedAggregateOptions | null>(() => (fromToDateOverride.value ? (fromToDateOverride.value.aggregateOptions || aggregateOptionsChart.value) : aggregateOptionsChart.value));

const chartLegendHeight = computed(() => (props.chartLegendHeightOverride ?? 100));

const noDataText = computed<string>(() => (props.noDataMessage || noDataTextOverride.value || Messages.NoData));

const noData = computed<boolean>(() => (moduleCreated.value && showNoData.value && !showChanging.value));
const allNoAccess = computed<boolean>(() => noData.value && showAllNoAccess.value);
const standardNoData = computed<boolean>(() => noData.value && !showAllNoAccess.value);
const partialAccessMissing = computed<string>(() => {
    let missingText = 'Some data on this chart requires higher access levels:\n';
    Object.values(seriesWithAccessLevels.value).forEach((series) => {
        missingText += ` - ${series.seriesName} series requires ${series.accessLevel} access.\n`;
    });
    return missingText;
});

const drilldownDisabled = computed<boolean>(() => props.disableDrilldownProp);

const aggregateHelpText = computed<string>(() => {
    const granularityText = aggregateGranularities.find(x => x.id === aggregateOptions.value?.granularity)?.info || '';

    const isCumulativeText = aggregateOptions.value?.cumulative ? 'Cumulative ' : '';
    const isDeltaText = aggregateOptions.value?.delta ? 'Delta ' : '';

    const helpString = `Aggregate data: ${isCumulativeText}${isDeltaText}${granularityText}${aggregateOptions.value?.type == null ? '' : aggregateOptions.value.type}`;
    return helpString.charAt(0).toUpperCase() + helpString.slice(1);
});

const isAggregate = computed<boolean>(() => !!aggregateOptions.value
        && (!!aggregateOptions.value.cumulative
         || !!aggregateOptions.value.delta
         || (!!aggregateOptions.value.granularity && !!aggregateOptions.value.type)));

const isCumulative = computed(() => aggregateOptions.value && aggregateOptions.value.cumulative);

const showFullDay = computed<boolean>(() => {
    const recentHour = props.chartConfig.info.date?.recentHour;
    const toHours = props.chartConfig.info.date?.toHours;
    const fromHours = props.chartConfig.info.date?.fromHours;

    if (recentHour && (toHours || fromHours)) {
        return false;
    }

    return otherSettings.value.data.fullDAChartAxes;
});

const shouldUseBoost = computed(() => !otherSettings.value.data.fullChartFunctionality && daysOnChart.value > 20);

const daysOnChart = computed(() => dayjs(toDate.value, 'DDMMYYYY').diff(dayjs(fromDate.value, 'DDMMYYYY'), 'days') + 1);

const allowPushes = computed(() => !props.noPushes && !isAggregate.value);

const otherSettings = computed(() => userSettingsStore.userSettings.otherSettings); // I added this for simplicity

const sitewideTimezone = computed<string>(() => userSettingsStore.sitewideTimezone);
const sitewidePeriodsPerDay = computed<number>(() => userSettingsStore.sitewidePeriodsPerDay);

const pageTimezoneOverride = computed<string | null>(() => props.customDateOverride?.chart?.timezone ?? userSettingsStore.pageChartTimezoneOverride(props.currentViewIndex) ?? null);

const pagePeriodsPerDay = computed<number | null>(() => props.customDateOverride?.chart?.periodsPerDay ?? userSettingsStore.pageChartPeriodsPerDayOverride(props.currentViewIndex) ?? null);

const timezoneToUse = computed<string>(() => {
    const moduleTimezone = userSettingsStore.moduleTimezone(props.currentViewIndex, props.currentModuleIndex);
    if (!props.allowSelectTimezone) return sitewideTimezone.value ?? DEFAULT_TIMEZONE;
    return pageTimezoneOverride.value ?? moduleTimezone ?? sitewideTimezone.value;
});

const periodsPerDayToUse = computed<number>(() => {
    if (!props.allowSelectTimezone) return sitewidePeriodsPerDay.value ?? DEFAULT_PERIODS_PER_DAY;
    if (props.customDateOverride?.chart?.periodsPerDay) return props.customDateOverride.chart.periodsPerDay;
    const periodsToUse = pagePeriodsPerDay.value ?? userSettingsStore.modulePeriodsPerDay(props.currentViewIndex, props.currentModuleIndex) ?? sitewidePeriodsPerDay.value;
    return periodsToUse;
});

const currentPeriod = computed(() => dataStore.periodData?.current.period ?? 1);

const isTime = computed(() => (Array.isArray(chartOptions.value!.xAxis) ? chartOptions.value!.xAxis[0].type === 'datetime' : chartOptions.value!.xAxis?.type === 'datetime'));

// #endregion

// #region methods

function getAdditionalDetails (id: string): IChartSeriesAdditionalDetails | null {
    const hasAdditionalDetails = props.chartConfig.info.additionalDetails && id in props.chartConfig.info.additionalDetails;
    if (hasAdditionalDetails) return props.chartConfig.info.additionalDetails![id];
    return null;
}

function leftRight (axisLabel: string, presetLabel: number | null | undefined): string {
    if (presetLabel !== null && presetLabel !== undefined) {
        if (presetLabel === 0) {
            return 'left';
        }
        return 'right';
    }
    const foundLabel = props.chartConfig.info.axisLeftOrRight?.find((entry: { label: string }) => entry.label === axisLabel);
    if (foundLabel) {
        return foundLabel.value;
    }

    return 'left';
}

function hasPresetLeftRight (axisLabel: string, presetSides: Array<{ item1: string; item2: number }>): number | null {
    const found = presetSides.find(labels => labels.item1 === axisLabel);
    return found ? found.item2 : null;
}

function setIsUsingBoost (usingBoost: boolean): void {
    isUsingBoost.value = usingBoost;
}

function openChartHelpModal (): void {
    if (!chartHelpModalVisible.value) {
        chartHelpModalVisible.value = true;
    }
}

function setExtraChartInfo (isFirstTime = false): void {
    const extraChartInfo = props.chartConfig.info.extraChartInfo;
    // Addition to grab additional details and set default aggregate options (granularity and type)
    // For some reason, the additional details are first stored in data (for the preview) and then info (for the actual chart)
    // This method gets called both times so if the first (.data) is null just grab the second (.info)
    const additionalDetails = props.chartConfig.data.additionalDetails ?? props.chartConfig.info.additionalDetails;
    const providedAggregateOptions = extraChartInfo?.aggregateOptionsProp;

    if (isFirstTime && hasProvidedAggregateOptions(providedAggregateOptions)) {
        aggregateOptionsChart.value = providedAggregateOptions;
        if (providedAggregateOptions.conversionOptions) {
            setupCurrencyPushes();
        }
    } else if (!aggregateOptionsChart.value) {
        const defaultAggregateOptions = getDefaultAggregateOptionsFromAdditionalDetails(additionalDetails);
        if (defaultAggregateOptions) {
            aggregateOptionsChart.value = defaultAggregateOptions;
        }
    }

    if (extraChartInfo != null) {
        if (extraChartInfo.showTime != null) {
            showTime.value = extraChartInfo.showTime;
            setTimePeriod();
        }
        if (extraChartInfo.currentTimeLine != null) {
            if (typeof extraChartInfo.currentTimeLine === 'boolean') {
                currentTimeLine.value = extraChartInfo.currentTimeLine ? '5min' : 'period';
            } else {
                currentTimeLine.value = extraChartInfo.currentTimeLine;
            }
            setCurrentPeriodArea();
            currentTimeLineChanged();
        }
        if (extraChartInfo.showLegend != null) {
            showLegend.value = extraChartInfo.showLegend;
            setLegend();
        }
    }

    if (extraChartInfo) emit('update:extra-chart-info', extraChartInfo);
}

function setYAxisLimitsFromExtraChartInfo (): void {
    const extraChartInfo = props.chartConfig.info.extraChartInfo;
    if (extraChartInfo?.yAxisLimits != null) {
        setYAxisLimits(extraChartInfo.yAxisLimits);
    }
}

function currentTimeLineChanged (): void {
    if (currentTimeLine.value === 'period') {
        clearMinuteInterval();
    } else {
        clearMinuteInterval();
        startMinuteInterval();
    }
}

function startMinuteInterval (): void {
    currentTimeInterval.value = setInterval(() => {
        const changed = setCurrentPeriodArea();
        if (changed) {
            updateRefOptions();
        }
    }, 5 * 1000);
}

function clearMinuteInterval (): void {
    clearInterval(currentTimeInterval.value ?? undefined);
}

function syncSeriesVisibilityIfShared (series: Series): void {
    if (!props.customDateOverride?.commonSettings?.sharedLegendToggling) return;

    const userOptions = series.userOptions;
    if (!userOptions.id) return;
    legendSync.setSeriesVisibility(userOptions.id, series.visible);
}

const legendClickHandler = new LegendItemClickHandler<Series>({
    onIsolate: isolateSeries,
    onSingleClickSync: syncSeriesVisibilityIfShared,
});

function handleLegendItemClick (this: Series): boolean {
    return legendClickHandler.handleClick(this);
}

function handleSeriesClick (this: Series): boolean {
    const userOptions = this.userOptions as unknown as ISeriesWithExtraOptions;

    if (!userOptions.canClick) return false;

    if (userOptions.plantName != null) {
        emit('open:plant-modal', userOptions.plantName);
    } else if (!drilldownDisabled.value) {
        emit('open:plant-drilldown-modal', userOptions.id, userOptions.name);
    }

    return false;
}

function isolateSeries (targetSeries: Series): void {
    const visibilityByIndex = resolveIsolateVisibility(
        targetSeries.chart.series.map(series => ({
            name: series.name,
            visible: series.visible,
        })),
        targetSeries.name,
    );

    targetSeries.chart.series.forEach((series, index) => {
        const shouldBeVisible = visibilityByIndex[index];
        series.setVisible(shouldBeVisible);

        if (props.customDateOverride?.commonSettings?.sharedLegendToggling) {
            const userOptions = series.userOptions as unknown as ISeriesWithExtraOptions;
            if (userOptions?.id) {
                legendSync.setSeriesVisibility(userOptions.id, shouldBeVisible);
            }
        }
    });
}

function closeChart (): void {
    if (props.chartConfig.info.id == null) return;
    emit('close', props.chartConfig.info.id);
}

function editChart (): void {
    if (props.chartConfig.info.id == null) return;
    emit('edit', props.chartConfig.info.id);
}

function openImageModal (): void {
    if (!imageModalConfigVisible.value) {
        imageModalConfigVisible.value = true;
    }
}

function openYAxisLimitsModal (): void {
    if (!yAxisLimitsModalVisible.value) {
        yAxisLimitsModalVisible.value = true;
    }
}

function closeImageModal (): void {
    imageModalConfigVisible.value = false;
}

function closeYAxisLimitsModal (): void {
    yAxisLimitsModalVisible.value = false;
}

function showFullscreen (): void {
    baseChartRef.value?.fullscreen();
}

function resize (): void {
    setAxisInfo();
    updateRefOptions();
    baseChartRef.value?.resize();
}

async function updateCurrency (): Promise<void> {
    updateAggregateOptions(aggregateOptionsChart.value);
    await getDataAndCreateChart();
}

function setupCurrencyPushes (): void {
    const dateOriginal = getActualDateFromConfigDate();

    if (props.currencyOverride && props.currencyOverride !== 'default' && currencyList.value.size > 0) {
        currencyList.value.forEach(currency => {
            const post = {
                dayFrom: dayjs(dateOriginal?.from).toISOString(),
                dayTo: dayjs(dateOriginal?.to).toISOString(),
                currencyFrom: currency,
                currencyTo: props.currencyOverride ?? '',
            };
            chartApi.getCurrencyScalars(post, chartAbortController.value?.signal).then(res => {
                currencyConversions.value[currency] = res.data.scalars;
            }).catch((error: unknown) => {
                if (!isAbortError(error)) throw error;
            });
        });
    }
}

function updateAggregateOptions (options: ICombinedAggregateOptions | null, persist = true): void {
    let aggregateOptionsClone: ICombinedAggregateOptions = {};
    if (options != null) aggregateOptionsClone = { ...options };
    if (props.currencyOverride && props.currencyOverride !== 'default') {
        aggregateOptionsClone.conversionOptions = {
            newCurrency: props.currencyOverride,
        };
    } else {
        delete aggregateOptionsClone.conversionOptions;
    }

    aggregateOptionsChart.value = aggregateOptionsClone;

    if (props.currentViewIndex !== -1 && props.currentModuleIndex !== -1) {
        const payload = {
            viewKey: props.currentViewIndex,
            moduleKey: props.currentModuleIndex,
            property: 'aggregateOptionsProp',
            value: aggregateOptionsClone,
        };
        userSettingsActions.updateExtraChartOptions(payload);
        if (persist) userSettingsActions.updateDashboards(undefined, props.currentViewIndex);
    }

    setChartOptions();
}

function showRerequest (): void {
    rerequest.value = true;
}

function markSeriesDataChanged (): void {
    seriesDataVersion += 1;
    seriesDataCache = null;
}

function queueCombineConfigAndData (): void {
    if (combineConfigAndDataQueued) return;
    combineConfigAndDataQueued = true;
    nextTick(() => {
        combineConfigAndDataQueued = false;
        combineConfigAndData().catch(() => {});
    });
}

async function getDataAndCreateChart (): Promise<void> {
    if (showTable.value && props.chartData === null) {
        await getTable();
        refreshChartDataInBackground();
        return;
    }

    showChanging.value = true;
    await getDataDoNotCreateChart();

    syncChartStateWithFetchedData();
    showChanging.value = false;
}

async function refreshChartDataInBackground (): Promise<void> {
    await getDataDoNotCreateChart();
    syncChartStateWithFetchedData();
}

function syncChartStateWithFetchedData (): void {
    combineConfigAndData();
    if (!hasScatter.value) {
        updateDataGrouping();
        updateDateRangeNavigator();
    }
    if (hasColumnAndOrdinal.value) {
        if (Array.isArray(chartOptions.value?.xAxis)) chartOptions.value.xAxis[0].ordinal = true; // Added type safety
    }
}

async function getDataDoNotCreateChart (): Promise<void> {
    if (showMetrics.value) getSummaryMetrics();

    setDateAndSeriesOnChart();
    await requestData();
    addYAxisPlotBands();
}

function getShallowRequests (dateOverride?: IAdvancedDatePickerModel): IModuleChartRequestPayload[] {
    const shallowRequests: IModuleChartRequestPayload[] = [];
    // may need a conversion from Date to Dayjs
    getDataForRequesting((endpoint: string, dateConverted: IDateRequestTwo | IDateRequestOne) => {
        if ('runSql' in dateConverted) return; // Need to do a type safety check
        if (!dateConverted.from || !dateConverted.to) return; // it's possible this is too restrictive and we should just default to today instead of returning out
        const dateRequest = getDateRequestObject(dateConverted.from, dateConverted.to);
        (request.value[endpoint] as IModuleChartRequestPayload[]).forEach((originalRequest: IModuleChartRequestPayload) => {
            const shallowClone = { ...originalRequest };
            shallowClone.dates = dateRequest;
            shallowClone.aggregateOptions = aggregateOptions.value;
            shallowClone.timezoneId = timezoneToUse.value;
            if (shallowClone.aggregateOptions && shallowClone.aggregateOptions.conversionOptions) {
                shallowClone.aggregateOptions.conversionOptions = aggregateOptionsChart.value?.conversionOptions ?? shallowClone.aggregateOptions.conversionOptions;
            }
            shallowRequests.push(shallowClone);
        });
    }, true, false, dateOverride);
    return shallowRequests;
}

async function getSummaryMetrics (): Promise<void> {
    showChanging.value = true;
    calculatingMetrics.value = true;
    metricCalcData.value = dayjs().format('Do MMMM YYYY, h:mm a');

    const metricRequestData: IModuleChartMetricsPayload = {
        data: getShallowRequests(),
        seriesWithData: seriesWithData.value,
        hiddenHeadings: hiddenHeadings.value,
    };

    await chartApi.getChartToSummaryData(metricRequestData, chartAbortController.value?.signal).then((response) => {
        metricsData.value = response.data;
    }).catch((error: unknown) => {
        if (!isAbortError(error)) throw error;
    }).finally(() => {
        calculatingMetrics.value = false;
        showChanging.value = false;
    });
}

async function userRefresh (): Promise<void> {
    await runExclusiveChartRefresh(() => hourlyRefresh());
}

async function hourlyRefresh (): Promise<void> {
    await requestData(false);
    combineConfigAndData();
}

async function handleChartPush (pushHolder: IChartPush): Promise<void> {
    if (isProcessingPush.value || isUsingBoost.value) return;
    isProcessingPush.value = true;

    if (pushHolder.refresh) {
        await requestData(false);
        combineConfigAndData();
        isProcessingPush.value = false;
        return;
    }

    const pushes = pushHolder.data;
    const chartComponent = baseChartRef.value;

    if (!pushes || !chartComponent || !Array.isArray(chartOptions.value?.xAxis)) {
        isProcessingPush.value = false;
        return;
    }

    if (showFullDay.value && isTime.value) {
        const latestPoint = pushes.data[pushes.data.length - 1];
        const earliestPoint = pushes.data[0];

        let minValue;
        let maxValue;

        if (latestPoint?.newPoint) {
            let latestTimeInPush;
            if (latestPoint.current?.objectPoint) {
                latestTimeInPush = latestPoint.current.objectPoint.x;
            } else {
                latestTimeInPush = latestPoint.current.arrayPoint[0];
            }

            const xAxisMax = getMaxTimeAxisValueForDate(latestTimeInPush);
            if (!chartSync.viewRange.value && latestTimeInPush > xAxisMax) maxValue = latestTimeInPush;
        }

        if (earliestPoint?.newPoint) {
            let earliestTimeInPush;

            if (earliestPoint.current.objectPoint) {
                earliestTimeInPush = earliestPoint.current.objectPoint.x;
            } else {
                earliestTimeInPush = earliestPoint.current.arrayPoint[0];
            }

            const xAxisMin = getMinTimeAxisValueForDate(earliestTimeInPush);
            if (!chartSync.viewRange.value && earliestTimeInPush < xAxisMin) minValue = earliestTimeInPush;
        }

        if (minValue || maxValue) {
            setXAxisMinMax(minValue, maxValue);
            updateRefOptions();
        }
    }

    const config = props.chartConfig.data.config.find((el: { id: string }) => el.id === pushes.id);

    for (let pointIndex = 0; pointIndex < pushes.data.length; pointIndex += 1) {
        const pointHolder = pushes.data[pointIndex];
        if (pointHolder == null) continue;

        const point = pointHolder.current || pointHolder;

        if (!allowPushes.value) continue;
        const index = chartComponent.getSeriesIndex(`${pushes.id}`);
        if (index < 0) {
            await requestData(false);
            combineConfigAndData();
            isProcessingPush.value = false;
            break;
        } else {
            const changeDate = dayjs(pushes.day, 'DDMMYYYY');
            const toDateM = dayjs(toDate.value, 'DDMMYYYY');
            const fromDateM = dayjs(fromDate.value, 'DDMMYYYY');

            if (props.chartConfig.info.date?.recentHour || changeDate.isBetween(fromDateM, toDateM, 'days', '[]')) {
                if (pushes.replaceSeries) {
                    await requestData(false);
                    combineConfigAndData();
                    isProcessingPush.value = false;
                    return;
                }
                let success = true;

                const xVal = point.arrayPoint[0];

                if (props.isDelta) {
                    point.arrayPoint = performDeltaPush(pushes.day, point.arrayPoint);
                }

                if (props.chartConfig.info.date?.recentHour) {
                    currentHourEnd.value = dayjs().unix();
                    currentHourStart.value = dayjs().subtract(1, 'hours').unix();

                    if (props.chartConfig.info.date.fromHours != null) {
                        currentHourStart.value = dayjs().subtract(props.chartConfig.info.date.fromHours, 'hours').unix();
                    }
                    if (props.chartConfig.info.date.fromHours) {
                        chartComponent.removeOldPoints(currentHourStart.value * 1000, index);
                    }
                    if (props.chartConfig.info.date.toHours != null) {
                        currentHourEnd.value = dayjs().add(props.chartConfig.info.date.toHours, 'hours').unix();
                    }
                } else {
                    const toHour = props.chartConfig.info.date?.toHoursForDay ?? '23:59';
                    const fromHour = props.chartConfig.info.date?.fromHoursForDay ?? '00:00';
                    currentHourEnd.value = dayjs(`${toDate.value} ${toHour}`, 'DDMMYYYY HH:mm').unix();
                    currentHourStart.value = dayjs(`${fromDate.value} ${fromHour}`, 'DDMMYYYY HH:mm').unix();
                }

                if ((config?.xSuffix === '' && config.xPrefix === '') || config?.xSuffix === 'Time') {
                    if (currentHourStart.value != null && (currentHourStart.value > xVal / 1000 || xVal / 1000 > currentHourEnd.value)) continue;
                }

                if (config && config.currency && props.currencyOverride && props.currencyOverride !== 'default' && config.currency in currencyConversions.value) {
                    const scalars = currencyConversions.value[config.currency];
                    if (pushes.day in scalars) {
                        if (point.arrayPoint) {
                            point.arrayPoint[1] *= scalars[pushes.day];
                        } else if (point.objectPoint) {
                            point.objectPoint.y *= scalars[pushes.day];
                        }
                    }
                }

                if (pointHolder.deletePoint) {
                    success = chartComponent.deletePoint(index, point);
                } else if (pointHolder.newPoint) {
                    success = chartComponent.addPoint(index, point);
                } else {
                    success = chartComponent.updatePoint(index, point);
                }

                if (!success) {
                    await requestData(false);
                    combineConfigAndData();
                    isProcessingPush.value = false;
                    return;
                }
            }
        }
    }

    chartComponent.redraw();

    if (showTable.value && props.chartData === null) {
        debounceTableRefresh();
    }

    isProcessingPush.value = false;
}

function pointHasPlottableValue (
    point: PointOptionsObject | Array<number | null> | null | undefined,
    seriesType: string | undefined,
): boolean {
    if (point == null) {
        return false;
    }

    if (Array.isArray(point)) {
        if (seriesType === 'arearange' || seriesType === 'columnrange') {
            return point[1] != null || point[2] != null;
        }
        return point.length >= 2 && point[1] != null;
    }

    if ('low' in point || 'high' in point) {
        return point.low != null || point.high != null;
    }

    return point.y != null;
}

function seriesHasPlottablePoints (
    data: Array<PointOptionsObject | Array<number | null>> | null | undefined,
    seriesType: string | undefined,
): boolean {
    if (data == null || data.length === 0) {
        return false;
    }

    return data.some((point) => pointHasPlottableValue(point, seriesType));
}

async function combineConfigAndData (): Promise<void> {
    const configWithData: Array<ITableSeries> = [];
    const preparedSeriesData = getPreparedSeriesData();
    seriesWithData.value = [];
    let haveData = false;

    let maxXAxisNumber = 0;
    let yAxisNumber = 0;

    hasDrilldown.value = false;
    hasColumnAndOrdinal.value = false;
    hasDataLabel.value = false;
    noDataTextOverride.value = null;

    currencyList.value = new Set();
    yAxisEnumToIndex.value = {};

    const configToUse = props.chartConfig.data.config;

    configToUse.forEach((originalSeries: ITableSeries) => {
        const isColumn = originalSeries.type === 'column';
        const pointRange = isColumn ? (pointRangeOverride.value ?? originalSeries.pointRange ?? null) : null;

        const pointPlacement = pointRange ? 0 : (pointPlacementOverride.value ?? originalSeries.pointPlacement ?? 0);

        const copiedData = cloneDeep({
            ...originalSeries,
            data: [],
        });

        if (isColumn) copiedData.pointRange = pointRange;
        else delete copiedData.pointRange;

        if (pointPlacement != null) copiedData.pointPlacement = pointPlacement;
        else delete copiedData.pointPlacement;

        if (copiedData.type === 'scatter') hasScatter.value = true;
        if (copiedData.type === 'column' && copiedData.ordinal === true) hasColumnAndOrdinal.value = true;

        if (copiedData.type === 'step' || (copiedData.type === 'line' && otherSettings.value.data.stepChartOption)) {
            copiedData.type = 'line';
            copiedData.step = true;
        }

        if (copiedData.type === 'arearangestep' || (copiedData.type === 'arearange' && otherSettings.value.data.stepChartOption)) {
            copiedData.type = 'arearange';
            copiedData.step = true;
        }

        const additionalDetails = getAdditionalDetails(copiedData.id);
        const hasAdditionalDetails = additionalDetails != null;

        if (hasAdditionalDetails && additionalDetails.dataLabelLocation !== 'none') {
            const currentPeriodTime = getTimeFromPeriod(currentPeriod.value, dayjs(), periodsPerDayToUse.value, timezoneToUse.value);
            const startPeriod = currentPeriodTime.valueOf();
            const endPeriod = currentPeriodTime.add(0.5, 'hour').valueOf();
            const current = dayjs().valueOf();

            hasDataLabel.value = true;

            const dataLabelLocation = additionalDetails.dataLabelLocation;

            const dataLabels: ITableSeries['dataLabels'] = {
                enabled: true,
                allowOverlap: true,
                formatter (): string | number | null {
                    let timeToCheck;

                    switch (dataLabelLocation) {
                        case 'start':
                            timeToCheck = startPeriod;
                            break;
                        case 'end':
                            timeToCheck = endPeriod;
                            break;
                        case 'current':
                            timeToCheck = current;
                            break;
                        case 'all':
                            timeToCheck = this.point.x;
                            break;
                        case 'name': {
                            const nonNullSeriesPoints = this.series.points.filter(el => el.y !== null);
                            if (this.point === nonNullSeriesPoints[nonNullSeriesPoints.length - 1]) {
                                return copiedData.name;
                            }
                            return null;
                        }
                    }

                    if (this.point.x === timeToCheck) {
                        return getHighChartsYValue(copiedData, this.point, this);
                    }

                    return null;
                },
            };

            copiedData.dataLabels = dataLabels;
        }

        if (hasAdditionalDetails && additionalDetails.dashStyle !== 'defaultBackend') copiedData.dashStyle = additionalDetails.dashStyle;
        if (hasAdditionalDetails && additionalDetails.lineWidth != null) {
            if (copiedData.type === 'scatter') {
                if (!copiedData.marker) copiedData.marker = {};
                copiedData.marker.radius = additionalDetails.lineWidth;
            } else {
                copiedData.lineWidth = additionalDetails.lineWidth;
            }
        }
        if (copiedData.type === 'line') {
            (copiedData as ITableSeries & { clip?: boolean }).clip = true;
        }
        if (hasAdditionalDetails && additionalDetails.opacity != null) copiedData.opacity = additionalDetails.opacity;
        if (hasAdditionalDetails && additionalDetails.prefix != null) copiedData.yPrefix = additionalDetails.prefix;
        if (hasAdditionalDetails && additionalDetails.suffix != null) copiedData.ySuffix = additionalDetails.suffix;
        if (hasAdditionalDetails && additionalDetails.stackGroup != null) copiedData.stack = additionalDetails.stackGroup;
        else if (copiedData.stack === 0) copiedData.stack = 1;

        const presetSides = props.chartConfig.data.axisSide;
        if (presetSides !== null && presetSides !== undefined) {
            copiedData.leftRight = presetSides;
        }

        if (copiedData.currency) {
            currencyList.value.add(copiedData.currency);
            if (props.currencyOverride != null && props.currencyOverride !== 'default') {
                const currentCurrencyHolder = dataStore.staticStore.currencies[copiedData.currency];
                const newCurrencyHolder = dataStore.staticStore.currencies[props.currencyOverride];

                copiedData.yPrefix = `${newCurrencyHolder.prefix}${copiedData.yPrefix.replace(currentCurrencyHolder.prefix, '').replace(currentCurrencyHolder.suffix, '')}`;
                copiedData.ySuffix = `${newCurrencyHolder.suffix}${copiedData.ySuffix.replace(currentCurrencyHolder.suffix, '').replace(currentCurrencyHolder.prefix, '')}`;
            }
        }

        copiedData.xAxis -= 1;

        copiedData.data = preparedSeriesData[copiedData.id];

        if (copiedData.ySuffix === 'Hz') {
            setFrequencyLine();
        }

        if (copiedData.canClick && !props.disableDrilldownProp) {
            hasDrilldown.value = true;
            if (copiedData.plantName == null) {
                hideDrilldownText.value = false;
            }
        }

        if (copiedData.noDataTextOverride) {
            noDataTextOverride.value = copiedData.noDataTextOverride;
        }

        maxXAxisNumber = Math.max(maxXAxisNumber, copiedData.xAxis);

        let shouldBoostZones = true;
        if (copiedData.zones) shouldBoostZones = false;
        copiedData.boostThreshold = (shouldUseBoost.value && shouldBoostZones) ? 500 : 0;
        if (copiedData.data.length > 0) {
            if (props.chartConfig.info.date?.recentHour) {
                currentHourEnd.value = dayjs().unix();
                currentHourStart.value = dayjs().subtract(1, 'hours').unix();

                if (props.chartConfig.info.date.fromHours != null) {
                    currentHourStart.value = dayjs().subtract(props.chartConfig.info.date.fromHours, 'hours').unix();
                }
                if (props.chartConfig.info.date.toHours != null) {
                    currentHourEnd.value = dayjs().add(props.chartConfig.info.date.toHours, 'hours').unix();
                }
            } else {
                const toHour = props.chartConfig.info.date?.toHoursForDay ?? '23:59';
                const fromHour = props.chartConfig.info.date?.fromHoursForDay ?? '00:00';

                currentHourEnd.value = dayjs(`${toDate.value} ${toHour}`, 'DDMMYYYY HH:mm').unix();
                currentHourStart.value = dayjs(`${fromDate.value} ${fromHour}`, 'DDMMYYYY HH:mm').unix();

                if (!props.isForIndex) {
                    const localOffset = dayjs().tz(DEFAULT_TIMEZONE).utcOffset();
                    const timezoneOffset = dayjs().tz(timezoneToUse.value).utcOffset();
                    const timezoneOffsetFromLocalSeconds = (timezoneOffset - localOffset) * 60;

                    currentHourStart.value -= timezoneOffsetFromLocalSeconds;
                    currentHourEnd.value -= timezoneOffsetFromLocalSeconds;
                }
            }

            if ((copiedData.xSuffix === '' && copiedData.xPrefix === '') || copiedData.xSuffix === 'Time') {
                copiedData.data = copiedData.data.filter((d: PointOptionsObject | Array<number>) => {
                    let xVal: number | undefined;
                    if (Array.isArray(d)) {
                        xVal = d[0] as number;
                    } else if (typeof d === 'object' && d !== null && 'x' in d) {
                        xVal = (d as { x: number }).x;
                    }
                    if (!xVal) return false;
                    return (currentHourStart.value! <= xVal / 1000 && xVal / 1000 <= currentHourEnd.value!);
                });
            }

            if (!seriesHasPlottablePoints(copiedData.data, copiedData.type)) {
                return;
            }

            const keyToEdit = `${copiedData.yPrefix}${copiedData.ySuffix}`;
            if (!(keyToEdit in yAxisEnumToIndex.value)) {
                yAxisEnumToIndex.value[keyToEdit] = yAxisNumber;
                yAxisNumber += 1;
            }
            copiedData.yAxis = yAxisEnumToIndex.value[keyToEdit];

            haveData = true;

            if (copiedData.opacity == null) {
                copiedData.opacity = 1;
            }

            configWithData.push(copiedData);
            seriesWithData.value.push(copiedData.id);
        }
    });

    setupCurrencyPushes();

    numberOfYAxis.value = yAxisNumber;

    numberOfXAxis.value = maxXAxisNumber;

    if (isTime.value && chartSync.viewRange.value) {
        setDateTimesFocussedAxis();
    } else if (showFullDay.value && isTime.value) {
        const earliestTimeInEachSeries: Array<number> = [];
        const latestTimeInEachSeries: Array<number> = [];
        configWithData.forEach((seriesConfig) => {
            if (seriesConfig.data.length > 0) {
                const firstDataPoint = seriesConfig.data[0];
                const lastDataPoint = seriesConfig.data[seriesConfig.data.length - 1];
                let xValEarliest: number | undefined;
                let xValLatest: number | undefined;

                if (Array.isArray(firstDataPoint)) {
                    xValEarliest = firstDataPoint[0] as number;
                } else if (typeof firstDataPoint === 'object' && firstDataPoint !== null && 'x' in firstDataPoint) {
                    xValEarliest = (firstDataPoint as { x: number }).x;
                }

                if (Array.isArray(lastDataPoint)) {
                    xValLatest = lastDataPoint[0] as number;
                } else if (typeof lastDataPoint === 'object' && lastDataPoint !== null && 'x' in lastDataPoint) {
                    xValLatest = (lastDataPoint as { x: number }).x;
                }

                if (xValEarliest !== undefined) {
                    earliestTimeInEachSeries.push(xValEarliest);
                }

                if (xValLatest !== undefined) {
                    latestTimeInEachSeries.push(xValLatest);
                }
            }
        });

        let minXValue;
        let maxXValue;

        if (latestTimeInEachSeries.length > 0) {
            const latestTimeInChart = Math.max(...latestTimeInEachSeries);
            const endOfDay = getMaxTimeAxisValueForDate(latestTimeInChart);
            maxXValue = Math.max(endOfDay, latestTimeInChart);
        }

        if (earliestTimeInEachSeries.length > 0) {
            const earliestTimeInChart = Math.min(...earliestTimeInEachSeries);
            const startOfDay = getMinTimeAxisValueForDate(earliestTimeInChart);
            minXValue = Math.min(startOfDay, earliestTimeInChart);
        }

        if (minXValue || maxXValue) setXAxisMinMax(minXValue, maxXValue);
    } else {
        // Needed to do some type checking
        let xAxis: XAxisOptions | undefined = {};
        if (Array.isArray(chartOptions.value?.xAxis)) {
            xAxis = chartOptions.value?.xAxis[0];
        } else {
            xAxis = chartOptions.value?.xAxis;
        }

        if (xAxis && ('max' in xAxis || 'min' in xAxis)) {
            setXAxisMinMax(undefined, undefined);
        }
    }

    chartOptions.value = chartOptions.value || {};
    chartOptions.value.series = configWithData as (SeriesOptionsType & ISeriesDataTooltipConfig)[]; // This is not safe, but is replicating what the old code did

    if (isCumulative.value) {
        for (let i = 0; i < chartOptions.value!.series.length; i += 1) {
            const series = chartOptions.value!.series[i] as SeriesOptionsType & ISeriesDataTooltipConfig; // This is not safe, but is replicating what the old code did
            series.ySuffix = (series.ySuffix === 'MW') ? 'MWh' : series.ySuffix;
        }
    }

    updateSharedYAxisRangeFromData(configWithData);
    setAxisInfo();
    setCurrentPeriodArea();
    setFormatterLocal();
    updateRefOptions();

    if (hasScatter.value) keyCounter.value += 1;
    showNoData.value = !haveData;
}

function getPreparedSeriesData (): NumericMatrixMap {
    const configKey = props.chartConfig.data.config.map(series => series.id).join('|');
    if (
        seriesDataCache
        && seriesDataCache.configKey === configKey
        && seriesDataCache.seriesDataVersion === seriesDataVersion
    ) return seriesDataCache.seriesData;

    const seriesData: NumericMatrixMap = {};
    props.chartConfig.data.config.forEach(series => {
        seriesData[series.id] = getNewDataForSeries(series.id);
    });
    seriesDataCache = {
        configKey,
        seriesDataVersion,
        seriesData,
    };
    return seriesData;
}

function setDateTimesFocussedAxis () : void {
    if (chartSync.viewRange.value && props.customDateOverride?.commonSettings?.sharedNivEvolutionViewfinder) {
        setXAxisMinMax(dayjs(chartSync.viewRange.value.from).valueOf(), dayjs(chartSync.viewRange.value.to).valueOf());
    } else setXAxisMinMax(undefined, undefined);
}

function setFocussedTime () : void {
    if (!baseChartRef.value || !isTime.value) return;

    baseChartRef.value.triggerTooltipHover(chartSync.hoveredTimestamp.value);
}

function getMaxTimeAxisValueForDate (baseTimestamp: number): number {
    // closest you can get to the end of the day without the next day's date showing on the highchart axis label
    const endOfDay = dayjs(baseTimestamp).tz(timezoneToUse.value).hour(23).minute(59)
        .second(29);
    return endOfDay.valueOf();
}

function getMinTimeAxisValueForDate (baseTimestamp: number): number {
    const startOfDay = dayjs(baseTimestamp).tz(timezoneToUse.value).hour(0).minute(0)
        .second(0);
    return startOfDay.valueOf();
}

function getNewDataForSeries (id: string): Array<Array<number>> {
    const newData: Array<Array<number>> = [];
    const addData = (data: Array<Array<number>> | null | undefined): void => {
        if (!data) return;
        for (let i = 0; i < data.length; i += 1) newData.push(data[i]);
    };

    if (id in propData.value && propData.value[id] !== null) {
        Object.values(propData.value[id]).forEach(realtimeSeriesOption => {
            addData(realtimeSeriesOption);
        });
    } else {
        if (id in historicData.value && historicData.value[id] != null) {
            addData(historicData.value[id]);
        }

        if (id in realTimeData.value && realTimeData.value[id] != null) {
            const datesSorted = Object.keys(realTimeData.value[id]).sort(compareDdMmYyyyDateStrings);

            datesSorted.forEach(date => {
                const realtimeSeriesOption = realTimeData.value[id];
                addData(realtimeSeriesOption[date]);
            });
        }
    }

    return newData;
}

function getActualDateFromConfigDate (): {
    from: Dayjs | null;
    to: Dayjs | null;
} | null {
    if (!props.chartConfig.info.date) return null;
    return getDatesFromConfig(
        props.chartConfig.info.date as IDateObject,
        fromDateOverride.value,
        toDateOverride.value,
    );
}

function getDateRequestObject (from: Dayjs, to: Dayjs): Array<IDateRangeComposite> {
    return [{
        fromDay: from.format('DD'),
        fromMonth: from.format('MM'),
        fromYear: from.format('YYYY'),
        toDay: to.format('DD'),
        toMonth: to.format('MM'),
        toYear: to.format('YYYY'),
    }];
}

function createBackendConfigAndSubscribe (getHistoric: boolean): Record<string, IModuleChartRequestPayload[]> {
    const data: Record<string, Array<IModuleChartRequestPayload>> = {};

    const pushesSubscribed: Array<string> = [];
    props.chartConfig.data.config.forEach((config: ITableSeries) => {
        if (config.isRealSeries) {
            const seriesOptionsString: IModuleChartRequestDataSeries = {
                Id: config.id,
                CalculatorIdArray: config.calculatorIdArray,
                Colour: null,
                ChartType: null,
                Name: config.name,
                Multiplier: config.multiplier,
                SkipAddingSeriesIfNoData: config.skipAddingSeriesIfNoData,
            };

            const toSend: IModuleChartRequestPayload = {
                series: seriesOptionsString,
                noHistorics: !getHistoric,
            };

            let pushGroup = config.id;
            if (config.calculatorIdArray) {
                let missingCalculatorOption = 'calc_hide';
                const additionalDetails = getAdditionalDetails(config.id);
                if (additionalDetails && additionalDetails.missingCalculatorOption) {
                    missingCalculatorOption = additionalDetails.missingCalculatorOption;
                }
                toSend.missingCalculatorOption = missingCalculatorOption;
                pushGroup = `${config.calculatorIdArray.join('@')}&${missingCalculatorOption}`;
            }

            const endpoint = resolveStaticApiEndpoint(config.endpoint, dataStore.staticStore.apiEndpointBaseUrlOverrides);
            if (endpoint != null) {
                if (!(endpoint in data)) data[endpoint] = [];
                data[endpoint].push(toSend);
            }

            if (allowPushes.value) {
                if (config.id.includes(historyOfForecastConst)) {
                    const id = config.id.split(historyOfForecastConst)[0];
                    let startDay = dayjs.max(dayjs(fromDate.value, 'DDMMYYYY'), dayjs().startOf('day'));
                    const endDay = dayjs(toDate.value, 'DDMMYYYY');
                    while (startDay.isSameOrBefore(endDay)) {
                        subscribeToForecastHistory(id, startDay, pushesSubscribed);
                        startDay = startDay.add(1, 'days');
                    }
                } else {
                    pushesSubscribed.push(pushGroup);
                    signalRLifecycle.joinGroup('series', pushGroup);
                    signalRLifecycle.listenTo(`chartUpdate&${pushGroup}`, handleChartPush);
                }
            }
        }
    });
    return data;
}

async function requestData (getHistoric = true): Promise<void> {
    markSeriesDataChanged();
    rerequest.value = false;
    showNoAccess.value = false;

    const addToSeriesStore = (toAddFrom: SeriesDataRaw, isPropData: boolean, isHistoricApi = false): void => {
        Object.keys(toAddFrom).forEach(id => {
            if (isPropData) propData.value[id] = {};
            else if (!(id in realTimeData.value)) {
                realTimeData.value[id] = {};
                rawRealTimeData.value[id] = {};
            }

            const dayKeys = Object.keys(toAddFrom[id]);
            if (isPropData) {
                dayKeys.forEach((day) => {
                    propData.value[id][day] = [];
                    propData.value[id][day] = toAddFrom[id][day];
                });
                return;
            }

            if (!(id in rawRealTimeData.value)) {
                rawRealTimeData.value[id] = {};
            }

            if (dayKeys.includes('SQL')) {
                writeHistoricSqlIfAllowed(historicData.value, id, toAddFrom[id].SQL, isHistoricApi);
            }

            mergeRealtimeDayBuckets(
                historicData.value[id],
                realTimeData.value[id],
                rawRealTimeData.value[id],
                toAddFrom[id],
            );
        });
    };

    if (props.chartData) {
        realTimeData.value = {};
        rawRealTimeData.value = {};
        if (Object.keys(props.chartData.seriesFrontEnd).length > 0) {
            addToSeriesStore(props.chartData.seriesFrontEnd, true);
        }
    } else {
        realTimeData.value = {};
        rawRealTimeData.value = {};
        if (getHistoric) historicData.value = {};
    }

    const axiosRequests: Array<Promise<void>> = [];
    let calcRequests: Array<IModuleChartRequestData> = [];

    const getRequest = (endpoint: string, from: Dayjs, to: Dayjs, endpointRequest: string | null = null): {shallowRequests: IModuleChartRequestPayload[]; requestApiCall: Promise<void> } => {
        const dateRequest = getDateRequestObject(from, to);
        const shallowRequests: Array<IModuleChartRequestPayload> = [];

        request.value[endpoint].forEach(x => {
            const shallowClone = { ...x };
            shallowClone.dates = dateRequest;
            shallowClone.timezoneId = timezoneToUse.value;
            shallowRequests.push(shallowClone);
        });

        const endpointToUse = endpointRequest || `${endpoint}/data/`;
        const customApi = useApi(undefined, endpointToUse);

        const requestApiCall = customApi.post<Array<IModuleChartRequestPayload>, ISeriesList>({
            endpoint: '',
            payload: shallowRequests,
            ...apiAbortOptions(chartAbortController.value?.signal),
        }).then((response) => addSeriesAndAccessLevels(response as {data: IAddSeriesAndAccessLevelsData}, addToSeriesStore, !!endpointRequest))
            .catch((error: unknown) => {
                if (isAbortError(error)) return;
                throw error;
            });
        return { shallowRequests, requestApiCall };
    };

    const isCurrencyConversion = props.currencyOverride && props.currencyOverride !== 'default';
    if (isAggregate.value || isCurrencyConversion) {
        const appServiceEndpoint = `${apiEndpointUrls.series}/ChartToCumulativeApi`;
        const customApi = useApi(undefined, appServiceEndpoint);
        axiosRequests.push(
            customApi.post<Array<IModuleChartRequestPayload>, ISeriesList>({
                endpoint: '',
                payload: getShallowRequests(),
                ...apiAbortOptions(chartAbortController.value?.signal),
            }).then((response) => addSeriesAndAccessLevels(response as {data: IAddSeriesAndAccessLevelsData}, addToSeriesStore))
                .catch((error: unknown) => {
                    if (isAbortError(error)) return;
                    throw error;
                }),
        );
    } else {
        await getDataForRequesting((endpoint: string, dateConverted: IDateRequestTwo | IDateRequestOne) => {
            if (!('runSql' in dateConverted)) return; // Need to do a type safety check
            if (dateConverted.runBuffer) {
                const { requestApiCall: bufferAxios } = getRequest(endpoint, dateConverted.fromBuffer!, dateConverted.toBuffer!);
                axiosRequests.push(bufferAxios);
            }
            if (dateConverted.runSql) {
                let appServiceEndpoint;
                let isCalculator = false;

                if (endpoint.includes('Calculator')) {
                    appServiceEndpoint = `${apiEndpointUrls.calc}/getData`;
                    isCalculator = true;
                } else {
                    appServiceEndpoint = `${apiEndpointUrls.series}/HistoricSeriesApi`;
                }

                const newRequest = getRequest(endpoint, dateConverted.fromSql!, dateConverted.toSql!, appServiceEndpoint);
                axiosRequests.push(newRequest.requestApiCall);
                if (isCalculator) calcRequests = calcRequests.concat(newRequest.shallowRequests);
            }
        }, getHistoric);
    }
    await Promise.allSettled(axiosRequests);

    if (!props.calculatorPreview && calcRequests.length > 0) {
        calculatorTimer.value = setInterval(() => {
            chartApi.updateCalcListening(calcRequests, chartAbortController.value?.signal).catch((error: unknown) => {
                if (!isAbortError(error)) throw error;
            });
        }, 30000);
    } else if (calculatorTimer.value) {
        clearInterval(calculatorTimer.value);
    }

    if (props.isDelta) {
        performDeltaPull();
    }
}

async function getDataForRequesting (handleRequest: (endpoint: string, dateConverted: IDateRequestOne | IDateRequestTwo) => void, getHistoric = true, convertDates = true, dateOverride: IAdvancedDatePickerModel | undefined = undefined): Promise<void> {
    const getDates = async (endpoint: string, fromDateParam: Dayjs | null, toDateParam: Dayjs | null): Promise<IDateRequestTwo> => {
        const fromSql = fromDateParam;
        let toSql = toDateParam;
        let fromBuffer = fromDateParam;
        const toBuffer = toDateParam;

        let runSql = true;
        let runBuffer = true;

        const getEarliestDateInBuffer = (endpointToCheck: string): dayjs.Dayjs | null => {
            const earliestDayInBuffer = dataStore.staticStore.endpointToEarliestBufferDate[endpointToCheck]?.earliestDayInBuffer;
            return earliestDayInBuffer ? dayjs(earliestDayInBuffer).startOf('day') : null;
        };

        const endpointKey = endpoint.toLowerCase();
        if (!(endpointKey in dataStore.staticStore.endpointToEarliestBufferDate) && dataStore.earliestBufferPromises[endpointKey]) {
            await promiseWithTimeout(dataStore.earliestBufferPromises[endpointKey], 5000).catch(() => {});
        }

        const bufferDateKey = findEndpointBufferDateKey(endpoint, dataStore.staticStore.endpointToEarliestBufferDate);
        const earliestDateInBuffer = bufferDateKey == null ? null : getEarliestDateInBuffer(bufferDateKey);

        if (earliestDateInBuffer == null || !earliestDateInBuffer.isValid()) {
            runBuffer = false;
        } else if (fromDateParam === toDateParam) {
            if (fromDateParam && fromDateParam >= earliestDateInBuffer) {
                runSql = false;
            } else {
                runBuffer = false;
            }
        } else {
            let foundCase = false;
            if (fromDateParam && fromDateParam >= earliestDateInBuffer) {
                runSql = false;
                foundCase = true;
            }

            if (toDateParam && toDateParam < earliestDateInBuffer) {
                runBuffer = false;
                foundCase = true;
            }

            if (!foundCase) {
                fromBuffer = earliestDateInBuffer;
                toSql = earliestDateInBuffer.clone().add(-1, 'day');
            }
        }

        return {
            runSql,
            runBuffer,
            fromSql,
            toSql,
            fromBuffer,
            toBuffer,
        };
    };

    request.value = createBackendConfigAndSubscribe(getHistoric);
    if (!Object.keys(request.value).length) {
        showAllNoAccess.value = false;
    }

    const dateOriginal = dateOverride ? getDayJsRangeFromDayOnlyDatePicker(dateOverride) : getActualDateFromConfigDate();

    async function processAsyncEndpoint (endpoint: string): Promise<void> {
        if (!convertDates) {
            if (dateOriginal == null) return;
            handleRequest(endpoint, dateOriginal);
        } else {
            const dateConverted = await getDates(endpoint, dateOriginal?.from ?? null, dateOriginal?.to ?? null);
            if (dateConverted == null) return;
            handleRequest(endpoint, dateConverted);
        }
    }

    await Promise.allSettled(Object.keys(request.value).map(endpoint => processAsyncEndpoint(endpoint)));
}

function performDeltaPull (): void {
    if (Object.keys(realTimeData.value).length > 1) {
        throw new Error('More than 1 Real Series on Forecast Evolution - Please contact LCP for help.');
    }

    const series = Object.values(realTimeData.value)[0];
    if (!series) return;

    Object.keys(series).forEach(day => {
        if (series[day] === null) return;
        series[day] = series[day].map((point) => performDeltaPush(day, point));
    });
}

function addAccessLevels (accessLevels: { [x: string]: { seriesName: string; accessLevel: string; hasAccess: boolean } }): void {
    Object.keys(accessLevels).forEach(level => {
        if (accessLevels[level].hasAccess) {
            showAllNoAccess.value = false;
        }
        if (!accessLevels[level].hasAccess && !(level in seriesWithAccessLevels.value)) {
            seriesWithAccessLevels.value[level] = accessLevels[level];
            showNoAccess.value = true;
        }
    });
}

function addSeriesAndAccessLevels (
    response: { data: IAddSeriesAndAccessLevelsData },
    addToSeriesStore: (toAddFrom: SeriesDataRaw, isPropData: boolean, isHistoricApi?: boolean) => void,
    isHistoricApi = false,
): void {
    const frontEndData = response.data.seriesFrontEnd || response.data.SeriesFrontEnd;
    const accessLevel = response.data.accessLevelsFrontEnd || response.data.AccessLevelsFrontEnd;

    pointRangeOverride.value = response.data.pointRange ?? null;
    pointPlacementOverride.value = response.data.pointPlacement ?? null;

    if (frontEndData && accessLevel && Object.keys(frontEndData).length > 0) {
        addToSeriesStore(frontEndData, false, isHistoricApi);
        addAccessLevels(accessLevel);
    }
}

function performDeltaPush (day: string | number, point: number[]): number[] {
    let subtractFrom = point[1];
    const configSeries = props.chartConfig.data.config;

    if (!configSeries || !props.nonDeltaSeries) return point;
    for (let i = configSeries.length - 1; i >= 0; i -= 1) {
        const key = configSeries.find((k: { seriesOrder: number }) => k.seriesOrder === i);
        if (!key) continue;
        if (!(key.id in props.nonDeltaSeries.seriesFrontEnd)) return point;

        const toDeltaFrom = props.nonDeltaSeries.seriesFrontEnd[key.id][day].find((prevPoint: number[]) => point[0] === prevPoint[0]);
        if (!toDeltaFrom || toDeltaFrom[1] == null) continue;

        subtractFrom = toDeltaFrom[1];
        break;
    }

    return [point[0], point[1] - subtractFrom];
}

function setDateAndSeriesOnChart (): void {
    if (!props.chartConfig.info.date) return;
    const { from, to } = getDatesFromConfig(
        props.chartConfig.info.date as IDateObject,
        fromDateOverride.value,
        toDateOverride.value,
    );

    if (!from || !to) return;

    fromDate.value = from.format('DDMMYYYY');
    toDate.value = to.format('DDMMYYYY');

    // Might need stricter type checking here
    if ((props.chartConfig.info.date as IDateRangeByNumberWithType).to.type === 'dynamic') {
        if ((props.chartConfig.info.date as IDateRangeByNumberWithType).to.value > 0) {
            toDate.value = dayjs().add((props.chartConfig.info.date as IDateRangeByNumberWithType).to.value, 'days').format('DDMMYYYY');
        }
    }

    if (props.chartConfig.info.date.recentHour) {
        dateDisplay.value = `Previous ${props.chartConfig.info.date.fromHours}h to Next ${props.chartConfig.info.date.toHours}h`;
    } else if (dayjs(fromDate.value, 'DDMMYYYY').isBefore(dayjs(toDate.value, 'DDMMYYYY')) || fromDate.value === toDate.value) {
        dateDisplay.value = dayjs(fromDate.value, 'DDMMYYYY').format('LL');
    } else {
        dateDisplay.value = `${dayjs(fromDate.value, 'DDMMYYYY').format('LL')} - ${dayjs(toDate.value, 'DDMMYYYY').format('LL')}`;
    }
}

function setChartHeight (): boolean {
    if (!chartOptions.value?.chart || !chartOptions.value.legend) return true;

    const spacingBottom = 5;
    const fixedMarginBuffer = 40;

    const currentMaxHeight = chartOptions.value.legend.maxHeight;
    const currentMarginBottom = chartOptions.value.chart.marginBottom;
    const targetMarginBottom = props.fixedLegend
        ? chartLegendHeight.value + fixedMarginBuffer + spacingBottom
        : undefined;

    chartOptions.value.legend.maxHeight = chartLegendHeight.value;

    if (targetMarginBottom !== undefined) {
        chartOptions.value.chart.marginBottom = targetMarginBottom;
        chartOptions.value.chart.spacingBottom = spacingBottom;
    } else {
        delete chartOptions.value.chart.marginBottom;
        delete chartOptions.value.chart.spacingBottom;
    }

    return currentMaxHeight === chartLegendHeight.value && currentMarginBottom === targetMarginBottom;
}

async function rebuildChart (): Promise<void> {
    setChartOptions();
    setExtraChartInfo();
    await getDataAndCreateChart();
    updateDataGrouping();
}

let chartRefreshChain: Promise<void> = Promise.resolve();

async function runExclusiveChartRefresh (refresh: () => Promise<void>): Promise<void> {
    const run = chartRefreshChain.then(async () => {
        dateChanging.value = true;
        try {
            await refresh();
        } finally {
            dateChanging.value = false;
        }
    });
    chartRefreshChain = run.catch(() => {});
    await run;
}

async function dateChange (aggregateOptionsProp?: ICombinedAggregateOptions | null): Promise<void> {
    if (aggregateOptionsProp !== undefined) {
        updateAggregateOptions(
            hasProvidedAggregateOptions(aggregateOptionsProp)
                ? aggregateOptionsProp
                : getClearedAggregateOptions(),
            false,
        );
    }

    await runExclusiveChartRefresh(() => rebuildChart());
}

async function refreshForDashboardDateOverride (): Promise<void> {
    if (dateChanging.value || props.chartData) return;
    if (props.chartConfig.data.rerequestOnYearChange) return;

    await runExclusiveChartRefresh(() => getDataAndCreateChart());
}

function toggleDatePicker (): void {
    if (fromToDateOverride.value) return;
    if (displayDateRange.value || !dateChanging.value) {
        displayTimeSelector.value = false;
        displayDateRange.value = !displayDateRange.value;
    }
}

function setChartOptions (): void {
    chartOptions.value = getChartOptions(chartUsesDarkTheme.value, props.isForIndex, timezoneToUse.value);

    if (chartOptions.value.xAxis && Array.isArray(chartOptions.value.xAxis) && chartOptions.value.xAxis[0].title) {
        chartOptions.value.xAxis[0].title.text = '';
    }

    if (chartOptions.value.yAxis && Array.isArray(chartOptions.value.yAxis) && chartOptions.value.yAxis[0].title) {
        chartOptions.value.yAxis[0].title.text = '';
    }

    setFormatterLocal();

    if (chartOptions.value.plotOptions?.series) {
        chartOptions.value.plotOptions.series.events = {
            hide (e: Event): void {
                const target = e.target as unknown as Series;
                hiddenHeadings.value.push(target.name);
            },
            show (e: Event): void {
                const target = e.target as unknown as Series;
                const index = hiddenHeadings.value.findIndex(el => el === target.name);
                hiddenHeadings.value.splice(index, 1);
            },
            click (this: Series): boolean {
                return handleSeriesClick.call(this);
            },
            legendItemClick (this: Series): boolean {
                return handleLegendItemClick.call(this);
            },
        };
    }

    chartOptions.value.legend = chartOptions.value.legend || {}; // Ensure it's initialised
    chartOptions.value.chart = chartOptions.value.chart || {}; // Ensure it's initialised

    // custom no data text
    chartOptions.value.lang = { noData: noDataText.value };

    if (props.moveLegendRight) {
        chartOptions.value.legend.layout = 'vertical';
        chartOptions.value.legend.verticalAlign = 'middle';
        chartOptions.value.legend.align = 'right';
        chartOptions.value.legend.width = undefined;
        chartOptions.value.legend.x = 0;
        chartOptions.value.chart.marginRight = 150;
    }

    setLegend();
    setCurrentPeriodArea();
    setAxisInfo();
    setChartHeight();
    setTooltipEvents();
}

function setTooltipEvents () : void {
    if (!chartOptions.value) return;

    chartOptions.value.plotOptions = chartOptions.value.plotOptions || {};
    chartOptions.value.plotOptions.series = chartOptions.value.plotOptions.series || {};
    chartOptions.value.plotOptions.series.point = props.customDateOverride?.commonSettings?.sharedTooltipAcrossModules
        ? getSyncTooltipHover(point => (point as { x: number }).x)
        : undefined;
}

function setAxisInfo (): void {
    const getAxisToDisplay = (originalAxis: string): string => (isCumulative.value && originalAxis === 'MW' ? 'MWh' : originalAxis);
    const applyRebuiltXAxisDefaults = (axisToAdd: IAxisToAdd, isTimeAxis: boolean): void => {
        axisToAdd.crosshair = true;
        axisToAdd.ordinal = isTimeAxis ? false : undefined;
    };

    const setAxisDetails = (axisNameToChartId: { [x: string]: number }, chartOptionAxis: XAxisOptions | XAxisOptions[] | YAxisOptions | YAxisOptions[], isXAxis: boolean): void => {
        if (Object.keys(axisNameToChartId).length > 1 && Array.isArray(chartOptionAxis)) {
            chartOptionAxis.splice(0, chartOptionAxis.length);

            let heightTotal = 0;

            // Original : const useStack = !this.xAxis && this.multipleFigures;
            // but this.xAxis is not defined in this context, could be based on isXAxis
            // but the old site isn't doing that so I'm going to ignore for now

            const useStack = props.chartConfig.info.axisPositions != null;
            let axesSetting: 'same' | 'automatic' | 'manual' = 'same';
            const sidePositions = props.chartConfig.info.axisSidePositions;
            if (sidePositions === null || sidePositions === undefined || !('axisSidePositions' in props.chartConfig.info)) {
                axesSetting = 'same';
            } else if (sidePositions[0] === '1') {
                axesSetting = 'automatic';
            } else {
                axesSetting = 'manual';
            }
            let presetSides = props.chartConfig.info.axisSidePreset;
            if (presetSides == null) {
                presetSides = props.chartConfig.data.axisSidePreset;
            }
            if (presetSides?.length === 0) {
                presetSides = undefined;
            }

            Object.keys(axisNameToChartId).forEach(axis => {
                if (isXAxis) {
                    hasTime.value = hasTime.value || axis === 'Time';
                    hasNonTime.value = hasNonTime.value || axis !== 'Time';
                }

                const axisToDisplay = getAxisToDisplay(axis);
                let presetAxisSide;
                const axisToAdd: IAxisToAdd = {
                    title: {
                        style: {
                            fontSize: '12px',
                        },
                        margin: 2,
                        text: axisToDisplay === 'Time' ? undefined : axisToDisplay,
                    },
                    endOnTick: false,
                    type: axis === 'Time' ? 'datetime' : 'linear',
                    stackLabels: { enabled: false },
                };
                if (isXAxis) applyRebuiltXAxisDefaults(axisToAdd, axis === 'Time');

                if (presetSides != null) {
                    axesSetting = 'manual';
                    presetAxisSide = hasPresetLeftRight(axis, presetSides);
                }

                if (useStack) {
                    const heightIndex = props.chartConfig.info.axisPositions?.findIndex((el: { label: string }) => el.label === axisToDisplay);
                    if (!props.chartConfig.info.axisPositions || heightIndex == null || heightIndex === -1) return;
                    const height = props.chartConfig.info.axisPositions[heightIndex].value ?? 0;

                    axisToAdd.offset = 0;
                    axisToAdd.top = `${heightTotal}%`;
                    axisToAdd.height = `${height - 2}%`;

                    if (axesSetting === 'same') {
                        axisToAdd.opposite = false;
                    } else if (axesSetting === 'automatic') {
                        axisToAdd.opposite = axisNameToChartId[axis] % 2 === 1;
                        if (axisToAdd.opposite) {
                            axisToAdd.offset = 30;
                        }
                    } else {
                        const side = leftRight(axis, presetAxisSide);
                        axisToAdd.opposite = (side === 'right');
                        if (axisToAdd.opposite) axisToAdd.offset = 30;
                    }

                    heightTotal += height;
                } else if (axesSetting === 'same') {
                    axisToAdd.lineWidth = 0;
                    axisToAdd.minorGridLineWidth = 0;
                    axisToAdd.minorTickLength = 0;
                    axisToAdd.lineColor = 'transparent';
                    axisToAdd.tickLength = 0;
                    axisToAdd.opposite = false;
                } else if (axesSetting === 'automatic') {
                    axisToAdd.lineWidth = 0;
                    axisToAdd.minorGridLineWidth = 0;
                    axisToAdd.minorTickLength = 0;
                    axisToAdd.lineColor = 'transparent';
                    axisToAdd.tickLength = 0;
                    axisToAdd.opposite = axisNameToChartId[axis] % 2 === 1;
                } else {
                    const side = leftRight(axis, presetAxisSide);
                    axisToAdd.lineWidth = 0;
                    axisToAdd.minorGridLineWidth = 0;
                    axisToAdd.minorTickLength = 0;
                    axisToAdd.lineColor = 'transparent';
                    axisToAdd.tickLength = 0;
                    axisToAdd.opposite = side === 'right';
                }
                if (!isXAxis) {
                    const { gridLineColor, labelColor } = getMultiYAxisGridColour(
                        axisNameToChartId[axis],
                    );
                    axisToAdd.gridLineColor = gridLineColor;
                    if (axisToAdd.title?.style) {
                        axisToAdd.title.style.color = labelColor;
                    }
                    axisToAdd.labels = {
                        style: {
                            color: labelColor,
                        },
                    };
                    axisToAdd.plotLines = [getYAxisZeroPlotLine(uiStore.theme === 'dark', props.isForIndex)];
                }
                chartOptionAxis.push(axisToAdd as YAxisOptions & XAxisOptions);
            });
        } else if (Object.keys(axisNameToChartId).length === 1 && Array.isArray(chartOptionAxis)) {
            const axisKey = Object.keys(axisNameToChartId)[0];
            const isTimeAxis = axisKey === 'Time';
            const needsAxisRebuild = chartOptionAxis.length !== 1;

            if (!needsAxisRebuild && chartOptionAxis[0] != null) {
                const existingAxis = chartOptionAxis[0] as XAxisOptions & YAxisOptions;
                if (existingAxis.title) {
                    existingAxis.title.text = (props.isSmallWidth || props.isSmallHeight || isTimeAxis)
                        ? undefined
                        : getAxisToDisplay(axisKey);
                }
                existingAxis.endOnTick = false;
                existingAxis.type = isTimeAxis ? 'datetime' : 'linear';
                if (isXAxis && isTimeAxis) existingAxis.ordinal = false;
                if (isXAxis) {
                    hasTime.value = hasTime.value || isTimeAxis;
                    hasNonTime.value = hasNonTime.value || !isTimeAxis;
                } else {
                    hasTime.value = hasTime.value || isTimeAxis;
                }
            } else {
                // Keep the new-site 2 -> 1 axis rebuild safeguard, but restore the
                // old-site time-axis behaviour that preserves real datetime gaps.
                chartOptionAxis.splice(0, chartOptionAxis.length);

                const axisToDisplay = getAxisToDisplay(axisKey);
                const axisToAdd: IAxisToAdd = {
                    title: {
                        style: {
                            fontSize: '12px',
                        },
                        margin: 2,
                        text: (props.isSmallWidth || props.isSmallHeight || isTimeAxis) ? undefined : axisToDisplay,
                    },
                    endOnTick: false,
                    type: isTimeAxis ? 'datetime' : 'linear',
                    stackLabels: { enabled: false },
                };

                if (isXAxis) applyRebuiltXAxisDefaults(axisToAdd, isTimeAxis);
                if (!isXAxis) {
                    axisToAdd.plotLines = [getYAxisZeroPlotLine(uiStore.theme === 'dark', props.isForIndex)];
                }

                if (isXAxis) {
                    hasTime.value = hasTime.value || isTimeAxis;
                    hasNonTime.value = hasNonTime.value || !isTimeAxis;
                } else {
                    hasTime.value = hasTime.value || isTimeAxis;
                }

                chartOptionAxis.push(axisToAdd as YAxisOptions & XAxisOptions);
            }
        }
    };

    hasTime.value = false;

    if (!chartOptions.value) return;
    chartOptions.value.yAxis = chartOptions.value.yAxis || [];
    chartOptions.value.xAxis = chartOptions.value.xAxis || [];

    // TODO: This is attempting to do a modification of the chartOptions object, I don't like this pattern
    setAxisDetails(yAxisEnumToIndex.value, chartOptions.value.yAxis, false);
    setAxisDetails(props.chartConfig.data.xAxisNameToChartId, chartOptions.value.xAxis, true);

    setYAxisLimitsFromExtraChartInfo();
    applySharedYAxisRange();
    applyHideYAxisLabels();
}

async function getTable (): Promise<void> {
    const fromDateMoment = dayjs(fromDate.value, 'DDMMYYYY');
    const twoDaysAgo = dayjs().subtract(2, 'days');

    const requestsWithDate = getShallowRequests();
    await chartApi.getChartToTableData(requestsWithDate, chartAbortController.value?.signal).then((response) => {
        tableData.value = response.data;
        showTable.value = true;
        showMetrics.value = false;
        showChart.value = false;

        if (fromDateMoment.isBefore(twoDaysAgo)) {
            tableIncludesHistoric.value = true;
        }
    }).catch((error: unknown) => {
        if (!isAbortError(error)) throw error;
    }).finally(() => {
        showChanging.value = false;
    });
}

function toggleTable (): void {
    if (Object.keys(request.value).length !== 0 && props.chartData === null) {
        showChanging.value = true;
        getTable();
    } else {
        showTable.value = true;
        showMetrics.value = false;
        showChart.value = false;
        showChanging.value = false;
    }

    setCurrentModuleOption('Table');
}

async function toggleChart (): Promise<void> {
    const wasShowingTableOrMetrics = showTable.value || showMetrics.value;

    showMetrics.value = false;
    showTable.value = false;
    showChart.value = true;

    if (wasShowingTableOrMetrics) {
        await nextTick();
        baseChartRef.value?.resize();
    }

    setCurrentModuleOption('Chart');
}

async function toggleMetrics (): Promise<void> {
    if (!showMetrics.value) {
        await getSummaryMetrics();
    }
    showTable.value = false;
    showChart.value = false;
    showMetrics.value = true;
    setCurrentModuleOption('Metrics');
}

function applyCurrentView (viewMode: ViewMode): void {
    switch (viewMode) {
        case ViewMode.Chart:
            toggleChart();
            break;
        case ViewMode.Table:
            toggleTable();
            break;
        case ViewMode.Metrics:
            toggleMetrics();
            break;
        default:
            toggleChart();
    }
}

function toggleTime (): void {
    showTime.value = !showTime.value;
    if (props.currentViewIndex !== -1 && props.currentModuleIndex !== -1) {
        const payload = {
            viewKey: props.currentViewIndex,
            moduleKey: props.currentModuleIndex,
            property: 'showTime',
            value: showTime.value,
        };
        userSettingsActions.updateExtraChartOptions(payload);
        if (!props.isFixedModule) userSettingsActions.updateDashboards(undefined, props.currentViewIndex);
    }
    setTimePeriod();
    setFormatterLocal();
    updateRefOptions();
}

type IExtremaPoint = { value: number | null; timestamp: number | null };

function findMinMax (data: unknown, axisLimits?: { min: number | null; max: number | null } | null): { min: IExtremaPoint; max: IExtremaPoint } {
    const limits: { min: number | null; max: number | null } | null = (() : { min: number | null; max: number | null } | null => {
        if (!axisLimits || typeof axisLimits !== 'object') return null;
        const min = Number.isFinite(axisLimits.min) ? axisLimits.min : null;
        const max = Number.isFinite(axisLimits.max) ? axisLimits.max : null;
        if (min == null && max == null) return null;
        if (min != null && max != null && min > max) return null;
        return { min, max };
    })();

    let min: IExtremaPoint = { value: Infinity, timestamp: null };
    let max: IExtremaPoint = { value: -Infinity, timestamp: null };

    if (!data || typeof data !== 'object') {
        return {
            min: { value: null, timestamp: null },
            max: { value: null, timestamp: null },
        };
    }

    const normaliseEntry = (entry: unknown): { timestamp: number | null; value: number | null } | null => {
        if (!entry) return null;
        if (Array.isArray(entry)) {
            const timestamp = typeof entry[0] === 'number' ? entry[0] : null;
            const value = typeof entry[1] === 'number' ? entry[1] : null;
            return { timestamp, value };
        }
        if (typeof entry === 'object') {
            const asRecord = entry as Record<string, unknown>;
            let timestamp: number | null = null;
            if (typeof asRecord.x === 'number') timestamp = asRecord.x;
            else if (typeof asRecord.timestamp === 'number') timestamp = asRecord.timestamp;
            else if (typeof asRecord.t === 'number') timestamp = asRecord.t;

            let value: number | null = null;
            if (typeof asRecord.y === 'number') value = asRecord.y;
            else if (typeof asRecord.value === 'number') value = asRecord.value;
            else if (typeof asRecord.v === 'number') value = asRecord.v;

            return { timestamp, value };
        }
        return null;
    };

    const checkValue = (timestamp: number | null, value: number | null): void => {
        if (value == null || Number.isNaN(value)) return;
        if (limits && ((limits.min != null && value < limits.min) || (limits.max != null && value > limits.max))) return;
        if (value < (min.value ?? Infinity)) min = { value, timestamp };
        if (value > (max.value ?? -Infinity)) max = { value, timestamp };
    };

    const processNode = (node: unknown): void => {
        if (Array.isArray(node)) {
            node.forEach((entry) => {
                const norm = normaliseEntry(entry);
                if (norm) checkValue(norm.timestamp, norm.value);
            });
            return;
        }
        if (node && typeof node === 'object') {
            Object.values(node as Record<string, unknown>).forEach(child => processNode(child));
        }
    };

    processNode(data);

    return {
        min: min.value === Infinity ? { value: null, timestamp: null } : min,
        max: max.value === -Infinity ? { value: null, timestamp: null } : max,
    };
}

function areYAxisLimitsEquivalent (existing: IAxisLimits[] | null | undefined, next: IAxisLimits[]): boolean {
    if (!existing) return false;
    if (existing.length !== next.length) return false;
    return existing.every((ex, i) => {
        const nx = next[i];
        return (ex.min ?? null) === (nx.min ?? null)
            && (ex.max ?? null) === (nx.max ?? null)
            && (ex.userMin ?? null) === (nx.userMin ?? null)
            && (ex.userMax ?? null) === (nx.userMax ?? null)
            && (ex.minPadding ?? null) === (nx.minPadding ?? null)
            && (ex.maxPadding ?? null) === (nx.maxPadding ?? null)
            && ((ex.dynamicExpand ?? false) === (nx.dynamicExpand ?? false));
    });
}

function changeYAxisLimits (axisLimits: IAxisLimits[]): void {
    const axes = Array.isArray(axisLimits) ? axisLimits : [axisLimits];
    const updatedAxisLimits: IAxisLimits[] = [];

    axes.forEach((raw, index) => {
        const dynamic = !!raw.dynamicExpand;

        let userMin: number | null = null;
        if (Number.isFinite(raw.userMin)) userMin = raw.userMin as number;
        else if (Number.isFinite(raw.min)) userMin = raw.min as number;

        let userMax: number | null = null;
        if (Number.isFinite(raw.userMax)) userMax = raw.userMax as number;
        else if (Number.isFinite(raw.max)) userMax = raw.max as number;

        if (!dynamic) {
            updatedAxisLimits[index] = {
                ...raw,
                dynamicExpand: false,
                userMin,
                userMax,
                min: userMin,
                max: userMax,
                minPadding: raw.minPadding ?? null,
                maxPadding: raw.maxPadding ?? null,
            };
            return;
        }

        const constraints = { min: userMin, max: userMax };

        const mergeLimits = (current: { min: number; max: number }, candidate: { min: IExtremaPoint; max: IExtremaPoint }): { min: number; max: number } => ({
            min: candidate.min.value != null ? Math.min(current.min, candidate.min.value) : current.min,
            max: candidate.max.value != null ? Math.max(current.max, candidate.max.value) : current.max,
        });

        const rt = findMinMax(realTimeData.value, constraints);
        let limits = { min: rt.min.value ?? Infinity, max: rt.max.value ?? -Infinity };

        if (historicData.value) {
            const hist = findMinMax(historicData.value, constraints);
            limits = mergeLimits(limits, hist);
        }

        updatedAxisLimits[index] = {
            ...raw,
            dynamicExpand: true,
            userMin,
            userMax,
            min: limits.min === Infinity ? null : limits.min,
            max: limits.max === -Infinity ? null : limits.max,
            minPadding: raw.minPadding ?? 0.05,
            maxPadding: raw.maxPadding ?? 0.05,
        };
    });

    const existing = props.chartConfig.info.extraChartInfo?.yAxisLimits;

    if (!areYAxisLimitsEquivalent(existing, updatedAxisLimits) && props.currentViewIndex !== -1 && props.currentModuleIndex !== -1) {
        const payload = {
            viewKey: props.currentViewIndex,
            moduleKey: props.currentModuleIndex,
            property: 'yAxisLimits',
            value: updatedAxisLimits,
        };
        userSettingsActions.updateExtraChartOptions(payload);
        if (!props.isFixedModule) userSettingsActions.updateDashboards(undefined, props.currentViewIndex);
    }

    setYAxisLimits(updatedAxisLimits);
    updateRefOptions();
}

function setCurrentTimeLine (type: string): void {
    currentTimeLine.value = type;
    currentTimeLineChanged();
    if (props.currentViewIndex !== -1 && props.currentModuleIndex !== -1) {
        const payload = {
            viewKey: props.currentViewIndex,
            moduleKey: props.currentModuleIndex,
            property: 'currentTimeLine',
            value: currentTimeLine.value,
        };
        userSettingsActions.updateExtraChartOptions(payload);
        if (!props.isFixedModule) userSettingsActions.updateDashboards(undefined, props.currentViewIndex);
    }
    setCurrentPeriodArea();
    updateRefOptions();
}

function setCurrentModuleOption (option: string): void {
    if (props.currentViewIndex !== -1 && props.currentModuleIndex !== -1) {
        const payload = {
            viewKey: props.currentViewIndex,
            moduleKey: props.currentModuleIndex,
            property: 'currentDisplayOption',
            value: option,
        };
        userSettingsActions.updateExtraChartOptions(payload);
        if (!props.isFixedModule) userSettingsActions.updateDashboards(undefined, props.currentViewIndex);
    }
}

function setLegendVisibility (show: boolean): void {
    showLegend.value = show;

    if (props.currentViewIndex !== -1 && props.currentModuleIndex !== -1) {
        const payload = {
            viewKey: props.currentViewIndex,
            moduleKey: props.currentModuleIndex,
            property: 'showLegend',
            value: show,
        };
        userSettingsActions.updateExtraChartOptions(payload);
        if (!props.isFixedModule) userSettingsActions.updateDashboards(undefined, props.currentViewIndex);
    }

    setLegend();
    updateRefOptions();
}

function updateMultipleAxisOptions (): void {
    setAxisInfo();
    keyCounter.value += 1;
}

function setLegend (): void {
    if (!chartOptions.value) return;
    chartOptions.value.legend = chartOptions.value.legend || {};
    chartOptions.value.legend.enabled = showLegend.value;
    chartOptions.value.chart = chartOptions.value.chart || {};
    chartOptions.value.chart.spacingBottom = 0;
}

function setFormatterLocal (): void {
    if (!chartOptions.value || !otherSettings.value.data.timeFormatValue) return;
    if (!chartOptions.value.series || chartOptions.value.series.length < 1) return;

    const timeFormatValue = otherSettings.value.data.timeFormatValue;
    applyDateTimeLabelFormats(chartOptions.value, timeFormatValue, showTime.value);

    chartOptions.value.tooltip = {
        ...getTooltip(timezoneToUse.value, periodsPerDayToUse.value ?? undefined, undefined, timeFormatValue),
        split: props.seperateTooltip || userSettingsStore.userSettings.otherSettings.data.chartHoverChoice,
    };
}

function setTimePeriod (): void {
    if (!chartOptions.value?.xAxis) return;
    if (Array.isArray(chartOptions.value.xAxis)) {
        chartOptions.value.xAxis.forEach((xAxis) => {
            if (!xAxis.title?.text) { // It's a time axis
                xAxis.labels = xAxis.labels || {};
                if (showTime.value) {
                    xAxis.labels.formatter = undefined;
                    xAxis.type = 'datetime';
                    xAxis.tickInterval = undefined;
                } else {
                    // TODO - check this is correct, toString didn't happen in the old code so I assume it
                    // throws an error on the old site as formatter has to return a string, not a number
                    xAxis.labels.formatter = (e): string => getPeriodFromTime(dayjs(e.value), periodsPerDayToUse.value, timezoneToUse.value).toString();
                    xAxis.type = 'linear';
                }
            }
        });
    } else {
        // Needed to add single axis edge case just in case
        chartOptions.value.xAxis.labels = chartOptions.value.xAxis.labels || {};
        if (showTime.value) {
            chartOptions.value.xAxis.labels.formatter = undefined;
            chartOptions.value.xAxis.type = 'datetime';
            chartOptions.value.xAxis.tickInterval = undefined;
        } else {
            // TODO - check this is correct, toString didn't happen in the old code so I assume it
            // throws an error on the old site as formatter has to return a string, not a number
            chartOptions.value.xAxis.labels.formatter = (e): string => getPeriodFromTime(dayjs(e.value), periodsPerDayToUse.value, timezoneToUse.value).toString();
            chartOptions.value.xAxis.type = 'linear';
        }
    }
}

// Edit: added single and multiple axis use case

function applyYAxisLimits (yAxis: YAxisOptions, axisLimit: IAxisLimits): void {
    const min = axisLimit.min ?? null;
    const max = axisLimit.max ?? null;
    const minPadding = axisLimit.minPadding ?? 0;
    const maxPadding = axisLimit.maxPadding ?? 0;

    if (min !== null && max !== null) {
        const range = max - min;
        yAxis.min = min - range * minPadding;
        yAxis.max = max + range * maxPadding;
    } else {
        yAxis.min = min === null ? null : min - Math.abs(min * minPadding);
        yAxis.max = max === null ? null : max + Math.abs(max * maxPadding);
    }

    yAxis.minPadding = axisLimit.minPadding ?? undefined;
    yAxis.maxPadding = axisLimit.maxPadding ?? undefined;
    (yAxis as unknown as Record<string, unknown>).userMin = axisLimit.userMin ?? null;
    (yAxis as unknown as Record<string, unknown>).userMax = axisLimit.userMax ?? null;
    (yAxis as unknown as Record<string, unknown>).dynamicExpand = axisLimit.dynamicExpand ?? false;
}

function setYAxisLimits (axisLimits: Array<IAxisLimits>): void {
    if (!chartOptions.value?.yAxis) return;
    if (Array.isArray(chartOptions.value.yAxis)) {
        chartOptions.value.yAxis.forEach((yAxis, i) => {
            const axisLimit = axisLimits[i];
            if (axisLimit === undefined) return;
            applyYAxisLimits(yAxis, axisLimit);
        });
    } else {
        const axisLimit = axisLimits[0];
        if (axisLimit === undefined) return;
        applyYAxisLimits(chartOptions.value.yAxis, axisLimit);
    }
}

function clearSharedYAxisBoundsOnChart (): void {
    if (!chartOptions.value?.yAxis) return;
    const clearOne = (yAxis: YAxisOptions): void => {
        yAxis.min = undefined;
        yAxis.max = undefined;
    };
    if (!Array.isArray(chartOptions.value.yAxis)) {
        clearOne(chartOptions.value.yAxis);
    } else {
        chartOptions.value.yAxis.forEach(clearOne);
    }
}

function applySharedYAxisRange (): void {
    if (!props.customDateOverride?.commonSettings?.useYAxisNormalisation || !chartOptions.value?.yAxis) return;

    if (!yAxisSync.sharedYAxisRange.value) {
        clearSharedYAxisBoundsOnChart();
        return;
    }

    const { min, max } = yAxisSync.sharedYAxisRange.value;

    if (!Array.isArray(chartOptions.value.yAxis)) {
        chartOptions.value.yAxis.min = min;
        chartOptions.value.yAxis.max = max;
    } else {
        const yAxisArray = chartOptions.value.yAxis;
        yAxisArray.forEach((yAxis, index) => {
            yAxis.min = min;
            yAxis.max = max;
            yAxisArray[index] = yAxis;
        });
    }
}

function updateSharedYAxisRangeFromData (configWithData: Array<ITableSeries>): void {
    if (!props.customDateOverride?.commonSettings?.useYAxisNormalisation) return;

    const extent = computeSharedYAxisExtents(
        chartOptions.value?.plotOptions,
        configWithData as unknown as SeriesOptionsType[],
        typeof chartOptions.value?.chart?.type === 'string' ? chartOptions.value.chart.type : undefined,
    );
    if (extent == null) return;

    yAxisSync.updateYAxisRange(extent.min, extent.max);
}

function applyHideYAxisLabels (): void {
    if (!props.chartConfig.info.hideYAxisLabels || !chartOptions.value?.yAxis || !chartOptions.value.chart) return;

    chartOptions.value.chart.spacingLeft = 5;

    if (!Array.isArray(chartOptions.value.yAxis)) {
        chartOptions.value.yAxis.labels = { enabled: false };
        if (chartOptions.value.yAxis.title) {
            chartOptions.value.yAxis.title.text = '';
        }
    } else {
        const yAxisArray = chartOptions.value.yAxis;
        yAxisArray.forEach((yAxis, index) => {
            yAxis.labels = { enabled: false };
            if (yAxis.title) {
                yAxis.title.text = '';
            }
            yAxisArray[index] = yAxis;
        });
    }
}

async function addYAxisPlotBands (): Promise<void> {
    function perc2color (perc: number): string {
        let r; let g; const b = 0;
        if (perc < 50) {
            r = 255;
            g = Math.round(5.1 * perc);
        } else {
            g = 255;
            r = Math.round(510 - 5.10 * perc);
        }
        const h = r * 0x10000 + g * 0x100 + b * 0x1;
        return `#${(`000000${h.toString(16)}`).slice(-6)}`;
    }

    const addDetails = props.chartConfig.info.additionalDetails;
    if (addDetails) {
        const seriesId = Object.keys(addDetails).find(el => addDetails[el].summaryGradingGranularity !== 'none'
            && addDetails[el].summaryGradingGranularity != null);
        if (seriesId != null) {
            calculatingPercentiles.value = true;

            const post: IGetSeriesSummaryPayload = {
                id: seriesId,
                granularity: addDetails[seriesId].summaryGradingGranularity,
                type: addDetails[seriesId].summaryGradingType,
            };

            let response;
            try {
                response = await chartApi.getSeriesSummary(post, chartAbortController.value?.signal);
            } catch (error: unknown) {
                if (isAbortError(error)) return;
                throw error;
            }

            if (!chartOptions.value) return;

            if (Array.isArray(chartOptions.value.yAxis)) {
                chartOptions.value.yAxis[0].plotBands = [];
                chartOptions.value.yAxis[0].plotLines = chartOptions.value.yAxis[0].plotLines || [];
                chartOptions.value.yAxis[0].plotLines.push({
                    color: 'rgb(0, 163, 199)',
                    width: 5,
                    value: response.data.average,
                    zIndex: 2,
                });

                const flip = addDetails[seriesId].summaryGradingType !== 'min';

                for (let percentile = 0; percentile <= 10; percentile += 1) {
                    if (Object.prototype.hasOwnProperty.call(response.data.percentiles, percentile + 1)) {
                        chartOptions.value.yAxis[0].plotBands.push({
                            color: perc2color(flip ? (10 - percentile) * 10 : percentile * 10),
                            zIndex: 1,
                            from: response.data.percentiles[percentile],
                            to: response.data.percentiles[`${percentile + 1}`],
                            label: {
                                text: `${(flip ? percentile + 1 : 10 - percentile) * 10}`,
                                align: 'left',
                                x: percentile % 2 === 0 ? +50 : 10,
                            },
                        });
                    }
                }
            }
            updateRefOptions();
            calculatingPercentiles.value = false;
        }
    }
}

function setCurrentPeriodArea (): boolean {
    const date = getTimeFromPeriod(currentPeriod.value, dayjs(), periodsPerDayToUse.value, timezoneToUse.value);
    let changed = false;

    if (!chartOptions.value) return false;

    chartOptions.value.xAxis = chartOptions.value.xAxis || [];

    if (Array.isArray(chartOptions.value.xAxis)) {
        chartOptions.value.xAxis.forEach((xAxis) => {
            if (!xAxis.title?.text) {
                let from; let
                    to;
                if (currentTimeLine.value === 'period') {
                    from = date.second(0).millisecond(0).valueOf();
                    const minutesPerPeriod = (HOURS_PER_DAY * MINUTES_PER_HOUR) / periodsPerDayToUse.value;
                    to = date.second(0).millisecond(0).add(minutesPerPeriod, 'minute').valueOf();
                } else {
                    const time = currentTimeLine.value === '5min' ? 0.05 : 0.01;
                    from = dayjs().second(0).millisecond(0).add(-time, 'hours')
                        .valueOf();
                    to = dayjs().second(0).millisecond(0).add(time, 'hours')
                        .valueOf();
                }

                if (xAxis.plotBands == null || xAxis.plotBands.length === 0 || xAxis.plotBands[0].from !== from) {
                    changed = true;
                    xAxis.plotBands = [{
                        color: uiStore.cssThemeVariables.chartGridLineColour,
                        from,
                        to,
                        zIndex: 2,
                    }];
                }
            }
        });
    } else if (!chartOptions.value.xAxis.title?.text) {
        let from; let to;
        if (currentTimeLine.value === 'period') {
            from = date.second(0).millisecond(0).valueOf();
            to = date.second(0).millisecond(0).add(0.5, 'hours').valueOf();
        } else {
            const time = currentTimeLine.value === '5min' ? 0.05 : 0.01;
            from = dayjs().second(0).millisecond(0).add(-time, 'hours')
                .valueOf();
            to = dayjs().second(0).millisecond(0).add(time, 'hours')
                .valueOf();
        }

        if (chartOptions.value.xAxis.plotBands == null || chartOptions.value.xAxis.plotBands.length === 0 || chartOptions.value.xAxis.plotBands[0].from !== from) {
            changed = true;
            chartOptions.value.xAxis.plotBands = [{
                color: uiStore.cssThemeVariables.chartGridLineColour,
                from,
                to,
                zIndex: 2,
            }];
        }
    }

    chartOptions.value.series?.forEach((series) => {
        if ('zoneAxis' in series && series.zoneAxis === 'x') {
            if (!('periodsToShiftZoneBy' in series)) return;
            if (!(series.zones)) return;
            const periodsToShiftZoneBy = series.periodsToShiftZoneBy as number;
            const newValue = date.clone().add(0.5 * (periodsToShiftZoneBy - 1), 'hours').valueOf();
            if (series.zones[0].value !== newValue) changed = true;
            series.zones[0].value = newValue;
        }
    });

    return changed;
}

function setFrequencyLine (): void {
    if (!chartOptions.value) return;
    chartOptions.value.yAxis = chartOptions.value.yAxis || [];
    if (Array.isArray(chartOptions.value.yAxis)) {
        chartOptions.value.yAxis[0].plotLines = [{
            color: '#212124',
            value: 50,
            width: 3,
            zIndex: 5,
        }];
    } else {
        chartOptions.value.yAxis.plotLines = [{
            color: '#212124',
            value: 50,
            width: 3,
            zIndex: 5,
        }];
    }
}

function updateDataGrouping (): void {
    if (props.hideNavigator) return;
    if (!chartOptions.value) return;
    // type safety stuff, probably can be simplified
    chartOptions.value.navigator = chartOptions.value.navigator || {};
    chartOptions.value.plotOptions = chartOptions.value.plotOptions || {};
    chartOptions.value.plotOptions.series = chartOptions.value.plotOptions.series || {};
    chartOptions.value.plotOptions.series.dataGrouping = chartOptions.value.plotOptions.series.dataGrouping || {};
    chartOptions.value.plotOptions.series.dataGrouping.approximation = isCumulative.value ? 'high' : undefined;
    updateRefOptions();
}

function setXAxisMinMax (minValue: number | undefined, maxValue: number | undefined): void {
    if (!chartOptions.value) return;
    if (Array.isArray(chartOptions.value.xAxis)) {
        chartOptions.value.xAxis[0].max = maxValue;
        if (chartOptions.value.xAxis.length > 1) chartOptions.value.xAxis[1].max = maxValue;

        chartOptions.value.xAxis[0].min = minValue;
        if (chartOptions.value.xAxis.length > 1) chartOptions.value.xAxis[1].min = minValue;
    } else {
        chartOptions.value.xAxis = chartOptions.value.xAxis || {};
        chartOptions.value.xAxis.max = maxValue;
        chartOptions.value.xAxis.min = minValue;
    }

    baseChartRef.value?.setXAxisExtremes(minValue, maxValue);
}

function updateDateRangeNavigator (): void {
    if (baseChartRef.value && !props.hideNavigator && chartOptions.value) {
        if (Array.isArray(chartOptions.value.xAxis)) {
            const retainMaxXValue = showFullDay.value && chartOptions.value.xAxis[0].type === 'datetime';
            baseChartRef.value?.setXAxisExtremes(null, retainMaxXValue ? chartOptions.value.xAxis[0].max : null);
        } else {
            chartOptions.value.xAxis = chartOptions.value.xAxis || {};
            const retainMaxXValue = showFullDay.value && chartOptions.value.xAxis.type === 'datetime';
            baseChartRef.value?.setXAxisExtremes(null, retainMaxXValue ? chartOptions.value.xAxis.max : null);
        }
    }
}

function setChartHover (): void {
    if (!chartOptions.value) return;
    chartOptions.value.tooltip = chartOptions.value.tooltip || {};
    chartOptions.value.tooltip.split = otherSettings.value.data.chartHoverChoice || props.seperateTooltip;
    if (props.calculatorPreview) {
        chartOptions.value.tooltip.outside = false;
    }
}

function updateRefOptions (): void {
    if (baseChartRef.value) {
        baseChartRef.value?.updateOptions(chartOptions.value);
    }
}

function getPaletteThemeChartUpdate (): Options {
    const paletteTheme = getTheme(uiStore.colourPaletteId);
    const axisThemeUpdate: Options = {
        xAxis: chartOptions.value?.xAxis,
        yAxis: chartOptions.value?.yAxis,
        legend: chartOptions.value?.legend,
        credits: chartOptions.value?.credits,
        title: chartOptions.value?.title,
        lang: chartOptions.value?.lang,
    };

    return {
        ...paletteTheme,
        ...axisThemeUpdate,
    };
}

function applyPaletteThemeToChart (): void {
    setChartOptions();
    if (!baseChartRef.value) return;
    baseChartRef.value.updateThemeOnly(getPaletteThemeChartUpdate());
}

function enqueueDeferredPaletteThemeRefresh (): void {
    const themeChanged = lastAppliedChartTheme.value !== uiStore.theme;

    enqueueChartPaletteRefresh(chartPaletteRefreshToken, async () => {
        setChartOptions();
        if (themeChanged) {
            await combineConfigAndData();
        }
        updateRefOptions();
    });

    lastAppliedChartTheme.value = uiStore.theme;
}

function downloadChart (): void {
    baseChartRef.value?.exportChart();
}

// #endregion

function editTable (event: Event): void {
    if (!genericTableRef.value) return;
    genericTableRef.value.showEditTableOverlay(event);
}

function triggerTableDownload (): void {
    if (genericTableRef.value) {
        genericTableRef.value.externalDownloadExcel();
    } else if (chartDataTableRef.value) {
        chartDataTableRef.value.downloadExcel();
    }
}

async function downloadTable (dateOverride?: IAdvancedDatePickerModel): Promise<void> {
    if (!props.chartData) {
        const requestsWithDate = getShallowRequests(dateOverride);
        try {
            const response = await chartApi.getChartToTableData(requestsWithDate, chartAbortController.value?.signal);
            tableData.value = response.data;
        } catch (error: unknown) {
            if (isAbortError(error)) return;
            throw error;
        }
    }

    await nextTick();

    triggerTableDownload();
}

async function getReportCsvRows (dateOverride?: IAdvancedDatePickerModel): Promise<Array<Array<string | number | null | undefined>>> {
    if (props.chartData) {
        return chartDataTableRef.value?.getReportCsvRows?.() ?? [];
    }

    const requestsWithDate = getShallowRequests(dateOverride);
    const response = await chartApi.getChartToTableData(requestsWithDate);
    return genericTableDataToCsvRows(response.data);
}

async function updatePageDate (fromTo: IDashboardOverrides): Promise<void> {
    await userSettingsStore.updatePageFromToDateOverride({
        fromTo,
        viewKey: props.currentViewIndex,
    });
}

defineExpose({
    chartOptions,
    showTime,
    showLegend,
    hasTime,
    currentTimeLine,
    toggleChart,
    toggleTable,
    toggleMetrics,
    updateCurrency,
    openChartHelpModal,
    closeChart,
    editChart,
    openImageModal,
    openYAxisLimitsModal,
    closeImageModal,
    closeYAxisLimitsModal,
    showFullscreen,
    downloadChart,
    resize,
    setupCurrencyPushes,
    updateAggregateOptions,
    showRerequest,
    getShallowRequests,
    userRefresh,
    hourlyRefresh,
    handleChartPush,
    toggleDatePicker,
    dateChange,
    rebuildChart,
    refreshForDashboardDateOverride,
    toggleTime,
    setLegendVisibility,
    changeYAxisLimits,
    updateMultipleAxisOptions,
    setCurrentTimeLine,
    downloadTable,
    getReportCsvRows,
    editTable,
    updatePageDate,
});

// #region Watchers

watch(numberOfYAxis, (newVal, oldVal) => {
    if (newVal !== oldVal && oldVal !== 0 && newVal !== 0) {
        keyCounter.value += 1;
    }
});

watch(numberOfXAxis, (newVal, oldVal) => {
    if (newVal !== oldVal && oldVal !== 0 && newVal !== 0) {
        keyCounter.value += 1;
    }
});

watch(() => (otherSettings.value.data.chartHoverChoice), () => {
    setChartHover();
    updateRefOptions();
});

watch(() => props.customDateOverride?.commonSettings?.sharedNivEvolutionViewfinder, setDateTimesFocussedAxis);

watch(currentPeriod, (newVal, oldVal) => {
    if (newVal === oldVal) return;

    if (hasDataLabel.value) {
        combineConfigAndData();
    } else {
        setCurrentPeriodArea();
    }

    updateRefOptions();

    if (currentPeriod.value === 1) {
        setDateAndSeriesOnChart();
        hourlyRefresh();
    }
});

watch(() => props.chartConfig, () => {
    getDataAndCreateChart();
    setChartOptions();
});

let lastChartOverrideKey = JSON.stringify(props.customDateOverride?.chart ?? null);
watch(() => props.customDateOverride?.chart, async (newVal) => {
    const key = JSON.stringify(newVal ?? null);
    if (key === lastChartOverrideKey) return;
    lastChartOverrideKey = key;
    await refreshForDashboardDateOverride();
}, { deep: true });

watch([periodsPerDayToUse, timezoneToUse], async (newVal, oldVal) => {
    if (newVal[0] === oldVal[0] && newVal[1] === oldVal[1]) return;
    await runExclusiveChartRefresh(() => rebuildChart());
});

let lastCommonSettingsKey = JSON.stringify(props.customDateOverride?.commonSettings ?? null);
let lastUseYAxisNormalisation = props.customDateOverride?.commonSettings?.useYAxisNormalisation ?? false;
watch(() => props.customDateOverride?.commonSettings, (newVal) => {
    const key = JSON.stringify(newVal ?? null);
    if (key === lastCommonSettingsKey) return;
    lastCommonSettingsKey = key;

    const useYAxisNorm = newVal?.useYAxisNormalisation ?? false;
    const yAxisNormChanged = useYAxisNorm !== lastUseYAxisNormalisation;
    lastUseYAxisNormalisation = useYAxisNorm;

    setTooltipEvents();

    if (yAxisNormChanged) {
        if (!useYAxisNorm) {
            yAxisSync.setSharedYAxisRange(null);
            clearSharedYAxisBoundsOnChart();
        }
        queueCombineConfigAndData();
        return;
    }

    updateRefOptions();
}, { deep: true });

watch(() => [otherSettings.value.data.stepChartOption, showFullDay.value], () => {
    queueCombineConfigAndData();
});

watch(() => otherSettings.value.data.timeFormatValue, () => {
    setFormatterLocal();
    updateRefOptions();
});

watch(() => uiStore.colourPaletteRevision, () => {
    if (skipDashboardPaletteRevision) return;
    applyPaletteThemeToChart();
    enqueueDeferredPaletteThemeRefresh();
});

watch(() => props.forceLightTheme, () => {
    setChartOptions();
    queueCombineConfigAndData();
});

watch(() => props.activeViewMode, (newView, oldView) => {
    if (newView === oldView) return;
    applyCurrentView(newView);
});

watch(chartSync.viewRange, () => {
    setDateTimesFocussedAxis();
});

watch(chartSync.hoveredTimestamp, () => {
    if (props.customDateOverride?.commonSettings?.sharedTooltipAcrossModules) {
        setFocussedTime();
    }
});

watch(yAxisSync.sharedYAxisRange, (range) => {
    applySharedYAxisRange();
    if (props.customDateOverride?.commonSettings?.useYAxisNormalisation && range == null) {
        queueCombineConfigAndData();
    }
    updateRefOptions();
});

watch(legendSync.sharedSeriesVisibility, (visibilityMap) => {
    if (!props.customDateOverride?.commonSettings?.sharedLegendToggling || !baseChartRef.value) return;
    baseChartRef.value.applySharedSeriesVisibility(visibilityMap);
});

watch(() => chartLegendHeight.value, () => {
    const heightSame = setChartHeight();
    if (!heightSame) updateRefOptions();
});

// I added this to make the tooltip update when the currency changes - in the old code it was triggered by a prop mutation to props.chartConfig that I tried replicating but it caused issues
watch(() => props.currencyOverride, () => {
    setFormatterLocal();
});

watch(() => isAggregate.value, () => {
    emit('update:isAggregate', isAggregate.value);
});

watch(noData, () => {
    emit('update:no-data', noData.value);
}, { immediate: true });

watch(allNoAccess, () => {
    emit('update:no-access', allNoAccess.value);
}, { immediate: true });

initialise();
// #endregion
</script>

<style scoped lang="scss">
.module-chart {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    padding: 0 var(--smallSpacing) var(--smallSpacing) 0;
    border-radius: var(--borderRadiusInner);

    &.chart-inner {
        background: var(--chartBackground);
    }

    .title {
        padding: var(--defaultSpacing);
        font-size: var(--largeFontSize);
    }

    .no-data {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--largeSpacing);
        text-align: center;

        &.access {
            font-size: var(--smallFontSize);
            padding: var(--defaultSpacing);

        }
    }
}
.has-title {
    height: calc(100% - 4rem);
}

.data-changing {
    position: absolute;
    left: 10px;
    padding: var(--smallSpacing);
    z-index: 1;
    color: var(--neutral900);

    &.bottom {
        top: auto;
        bottom: 0;
    }

    .drilldown {
        background-color: rgba(0, 0, 0, 0.3);
        border-radius: var(--borderRadiusInner);
    }

    .refresh-button {
        padding: 0;
    }

    &.top {
        top: 3px;
        bottom: auto;
    }
}
.data-changing-table {
    padding-top: var(--defaultSpacing);
    font-size: var(--smallFontSize);
}

// style brought over from moduleChartWithOptions
.module-table {
    padding: 0 0 0 var(--smallSpacing);
    display: grid;
    grid-template-rows: 1fr auto;
    height: 100%;
    overflow: hidden;

    .module-table-inner{
        height: 100%;
        overflow: hidden;
    }
}

</style>
